/**
 * RegionGrowTool — one-click flood-fill segmentation (V2 S3).
 *
 * On click: flood-fill by HU range → fill holes → extract outer boundary →
 * Chaikin smooth → create preview contour annotation.
 * Enter: accept preview (contour stays, becomes a real keyframe).
 * Escape: reject preview (contour removed).
 * Shift+click: add seed point, re-run fill with all seeds combined.
 * Ctrl+click: add exclusion point, subtract exclusion fill from result.
 * Ctrl+Z during preview: undo last seed/exclusion.
 *
 * Factory function pattern — same as BumpContourTool.
 */

import { getDefaultStore } from "jotai";
import type { RegionGrowConfig } from "../hooks/useLabelingQueries";
import {
  segEngineAtom,
  isSegmentationActiveAtom,
  nnInteractiveBlockedAtom,
  companionMasksBlockedAtom,
} from "../atoms/segmentationAtoms";
import { getActiveScope, pushUndo } from "./annotationUndo";
import { emit } from "../lib/viewerBus";
import { extractContourEdges, chaikinSmooth } from "../utils/contourUtils";
import { perfMark } from "../utils/performanceTiming";
import {
  initializeNnSession,
  isNnInteractiveAvailable,
  getNnRequestInFlight,
  setNnRequestInFlight,
  getMultiSessions,
  getNnContext,
  setNnFailedAt,
  showNnToast,
  getSiblingViewports,
  setHandleMultiSeriesNnClick,
  handleMultiSeriesNnClick,
  setCachedContext as _setCachedContext,
  hasNnInteractiveSession as _hasNnInteractiveSession,
  undoNnInteractive as _undoNnInteractive,
  rejectNnInteractive as _rejectNnInteractive,
  ensureSessionForFallback,
  refreshViewerFromAnnotations,
  resetConsecutiveOversizedCount,
  snapCompanionSeed,
  isFallbackOnly,
  worldToNativeVoxelZYX,
  notifyNnCrossViewportSync,
  PLANE_KEYS,
  captureGeometryForViewport,
  restoreGeometryCheckpoint,
  captureNnStateForThresholdUndo,
  type PlaneKey,
} from "./nnInteractiveSession";

// Re-export NN types, callbacks, and functions so external callers don't need to change imports
export { type PlaneKey } from "./nnInteractiveSession";
export {
  setNnInteractiveLoadingCallback,
  setNnInteractiveLayoutCallback,
  setGetSiblingViewportsCallback,
  setNnSegmentationResultCallback,
  setNnInteractiveContext,
  setNnToastCallback,
  setNnClickMarkersCallback,
  setNnSessionActiveCallback,
  refineNnInteractive,
  getMultiSessions,
  refreshViewerFromAnnotations,
  ensureSessionForFallback,
  endNnSession,
} from "./nnInteractiveSession";

type Pt2 = [number, number];
type Pt3 = [number, number, number];

// ─── Contrast-aware FWHM constants (#167) ────────────────────────────────────
// Pure helpers and constants live in regionGrowHelpers.ts so nnInteractiveSession
// can reuse them without a circular import. Re-exported below for the existing
// test imports (RegionGrowTool.test.ts).
import {
  DEFAULT_FWHM_FLOOR,
  CONTRAST_FWHM_FLOOR,
  DEFAULT_HU_TOLERANCE,
  CONTRAST_HU_TOLERANCE,
  floodFill2D,
  adaptiveMinHU,
  sampleBackgroundMeanHU,
} from "./regionGrowHelpers";
import { logger } from "@/utils/logger";
import { getCornerstone } from "../utils/dicomLoader";

export { floodFill2D, adaptiveMinHU } from "./regionGrowHelpers";

// ─── Module-level state ──────────────────────────────────────────────────────

let _config: RegionGrowConfig | null = null;

export function setRegionGrowConfig(config: RegionGrowConfig | null): void {
  _config = config;
}

let _segmentIndex = 1;

export function setRegionGrowSegmentIndex(index: number): void {
  _segmentIndex = index;
}

/** Per-viewport callbacks notifying VolumeViewport that a region-grow
 *  annotation was created. Keyed by `viewport.id` because in a multi-plane
 *  hanging protocol (axial + coronal + sagittal), each VolumeViewport
 *  captures its own `seriesId` in the closure and we need to fire the one
 *  that owns the clicked viewport — not "whichever mounted last".
 *
 *  Dispatch helpers (`fireRegionGrowCommit` / `fireRegionGrowAutoSwitch`)
 *  resolve by viewportId at fire time. Orphan dispatches (viewportId
 *  unknown or unregistered) log and return rather than fanning out —
 *  firing every callback would mean multiple `setLockedSeriesId` writes
 *  with different seriesIds racing each other, which is worse than a
 *  no-op while we investigate the orphan via the warn. */
const _regionGrowCommitCallbacks = new Map<string, (currentImageId: string | null) => void>();
const _regionGrowAutoSwitchCallbacks = new Map<string, () => void>();

export function setRegionGrowCommitCallback(
  viewportId: string,
  cb: ((currentImageId: string | null) => void) | null,
): void {
  if (cb) _regionGrowCommitCallbacks.set(viewportId, cb);
  else _regionGrowCommitCallbacks.delete(viewportId);
}

export function setRegionGrowAutoSwitchCallback(viewportId: string, cb: (() => void) | null): void {
  if (cb) _regionGrowAutoSwitchCallbacks.set(viewportId, cb);
  else _regionGrowAutoSwitchCallbacks.delete(viewportId);
}

function fireRegionGrowCommit(viewportId: string | null, currentImageId: string | null): void {
  if (viewportId && _regionGrowCommitCallbacks.has(viewportId)) {
    _regionGrowCommitCallbacks.get(viewportId)!(currentImageId);
    return;
  }
  // Orphan: don't fan out — competing setLockedSeriesId writes from
  // sibling viewports would corrupt drawing state worse than dropping
  // the commit. Surface a user-visible toast so a stuck Save button has
  // a reason rather than appearing silently broken.
  if (_regionGrowCommitCallbacks.size > 0) {
    logger.warn("[RegionGrowTool] commit dropped — no callback for viewportId", {
      viewportId,
      registered: Array.from(_regionGrowCommitCallbacks.keys()),
    });
    emit("toast", { message: "Region grow didn't register — click the viewport and try again." });
  }
}

/** Wrap fireRegionGrowCommit as a (currentImageId) => void closure for
 *  call sites that hand the callback off to `handleMultiSeriesNnClick`. */
function makeCommitDispatcher(viewportId: string | null): (currentImageId: string | null) => void {
  return (currentImageId) => fireRegionGrowCommit(viewportId, currentImageId);
}

function makeAutoSwitchDispatcher(viewportId: string | null): () => void {
  return () => fireRegionGrowAutoSwitch(viewportId);
}

function fireRegionGrowAutoSwitch(viewportId: string | null): void {
  if (viewportId && _regionGrowAutoSwitchCallbacks.has(viewportId)) {
    _regionGrowAutoSwitchCallbacks.get(viewportId)!();
    return;
  }
  // autoSwitch is the tool-mode flip after commit; missing it leaves the
  // user in region-grow mode when they expect to be in bump/edit. Toast
  // already fired from fireRegionGrowCommit covers the parent failure;
  // we just log here to avoid double-toasting on the same orphan.
  if (_regionGrowAutoSwitchCallbacks.size > 0) {
    logger.warn("[RegionGrowTool] autoSwitch dropped — no callback for viewportId", {
      viewportId,
      registered: Array.from(_regionGrowAutoSwitchCallbacks.keys()),
    });
  }
}

/** Preview annotation UID — non-null means we have an uncommitted preview. */
let _previewUID: string | null = null;

/** Image ID of the slice the current preview is on. */
let _previewImageId: string | null = null;

/**
 * The preview's other contours: blobs that Shift+Click seeds grew apart from
 * the largest one (`_previewUID`). Same slice and segment; removed with it.
 */
let _previewExtraUIDs: string[] = [];

/** Whether `uid` is one of the preview's contours. */
function isPreviewUID(uid: string | undefined): boolean {
  return !!uid && (uid === _previewUID || _previewExtraUIDs.includes(uid));
}

/** Remove every preview contour from annotation state (the UIDs are kept). */
function removePreviewAnnotations(cornerstoneTools: any): void {
  for (const uid of [_previewUID, ..._previewExtraUIDs]) {
    if (!uid) continue;
    try {
      cornerstoneTools.annotation.state.removeAnnotation(uid);
    } catch (e) {
      logger.debug("[RegionGrowTool] removeAnnotation skipped:", e);
    }
  }
}

/** Seed points [row, col] for current preview (first seed = original click). */
const _seeds: Pt2[] = [];

/** Exclusion points [row, col] for current preview. */
const _exclusions: Pt2[] = [];

/** Cached context for re-running fill without re-reading image data. */
let _cachedContext: {
  viewport: any;
  element: any;
  scalarData: any;
  imageData: any;
  cols: number;
  rows: number;
  effectiveMin: number;
  effectiveMax: number;
  maxRadiusPx: number;
  smoothingIter: number;
} | null = null;

/** Whether we have an active preview that can be accepted/rejected. */
export function hasPreview(): boolean {
  return _previewUID !== null;
}

export function resetRegionGrowState(): void {
  _thresholdGeneration += 1;
  _previewUID = null;
  _previewExtraUIDs = [];
  _previewImageId = null;
  _seeds.length = 0;
  _exclusions.length = 0;
  _cachedContext = null;
  _setCachedContext(null);
  _propagatedUIDs = [];
  _fallbackPerPlane.clear();
  _fallbackPrimaryPlane = null;
  // Multi-series nnInteractive state
  getMultiSessions().clear();
  setNnRequestInFlight(false);
  resetConsecutiveOversizedCount();
  bumpFallbackCompanionToken();
}

/** Tracks which slice imageIds used region grow during the current annotation. */
const _regionGrowImageIds = new Set<string>();

/**
 * Click-2 confirm for region grow on a slice that already has an in-progress
 * contour for the current segment: first click warns, second click within
 * OVERWRITE_CONFIRM_WINDOW_MS proceeds. Reset by any click on an
 * unannotated slice and by resetRegionGrowTracking (save / cancel / delete).
 */
const OVERWRITE_CONFIRM_WINDOW_MS = 3000;
let _pendingOverwriteSlice: { imageId: string; at: number } | null = null;

/** UIDs of annotations created by auto-propagation (for batch undo). */
let _propagatedUIDs: string[] = [];

/** Per-companion-plane bucket of contour UIDs created by the GPU-fallback
 *  flow. Tracks each COMPANION separately so Ctrl+Z can clear one view at
 *  a time (the user's active plane). The PRIMARY's contour is tracked in
 *  `_previewUID` + `_propagatedUIDs` as usual — the propagation pipeline
 *  reassigns `_propagatedUIDs` 16ms after the click, so we can't safely
 *  share that reference here without it going stale. */
type FallbackPlaneEntry = {
  seedUID: string;
  propagatedUIDs: string[];
  element: any;
};
const _fallbackPerPlane = new Map<PlaneKey, FallbackPlaneEntry>();

/** Which plane the user originally clicked when fallback fired. Drives
 *  per-plane undo: if the active plane equals this, undo targets the
 *  global primary state; otherwise it targets `_fallbackPerPlane`. Null
 *  when no fallback is active. */
let _fallbackPrimaryPlane: PlaneKey | null = null;

/** Monotonic token bumped whenever the user cancels (Esc/reject) or a new
 *  fallback flow starts. `runFallbackOnCompanions` and `runFallbackForCompanion`
 *  capture the token at entry and bail after every await if it has changed —
 *  prevents fire-and-forget companion writes from committing into a freshly
 *  cleared `_fallbackPerPlane` map. */
let _fallbackCompanionToken = 0;
let _thresholdGeneration = 0;
function bumpFallbackCompanionToken(): number {
  _fallbackCompanionToken += 1;
  return _fallbackCompanionToken;
}

export function getRegionGrowImageIds(): Set<string> {
  return new Set(_regionGrowImageIds);
}

export function resetRegionGrowTracking(): void {
  _regionGrowImageIds.clear();
  _pendingOverwriteSlice = null;
}

/** Drop a single imageId from the region-grow tracking set. Used when a
 *  single-slice contour is deleted (Delete key) so the save payload no
 *  longer reports that slice as region-grow-sourced. */
export function removeRegionGrowImageId(imageId: string): void {
  if (imageId) _regionGrowImageIds.delete(imageId);
}

/** Record that the region-grow tool committed on `imageId`. Exposed for tests
 *  and for any future external committer (production adds via direct mutation
 *  inside the tool class). */
export function addRegionGrowImageId(imageId: string): void {
  if (imageId) _regionGrowImageIds.add(imageId);
}

// ─── Shared annotation builder ──────────────────────────────────────────────

/** Build a PlanarFreehandContourSegmentationTool annotation from world points.
 *  Single source of truth — used by updatePreview and undoRegionGrowSeed. */
function buildContourAnnotation(
  worldPoints: Pt3[],
  ctx: NonNullable<typeof _cachedContext>,
  cornerstoneOrUtils: { uuidv4?: () => string },
  cornerstoneTools: any,
): { annotation: any; uid: string } {
  const imageId = ctx.viewport.getCurrentImageId?.();
  // Get segmentation ID from THIS viewport's active segmentation, not the global list.
  // The renderer checks getActiveSegmentation(viewportId) for visibility — if we use a
  // different segmentationId, the contour will be invisible.
  const activeSeg = cornerstoneTools.segmentation.getActiveSegmentation?.(ctx.viewport.id);
  const segId =
    activeSeg?.segmentationId ??
    cornerstoneTools.segmentation.state.getSegmentations()?.[0]?.segmentationId ??
    "unknown";
  const camera = ctx.viewport.getCamera?.() ?? {};
  const viewPlaneNormal = camera.viewPlaneNormal ?? [0, 0, 1];
  const viewUp = camera.viewUp ?? [0, -1, 0];
  const allImageIds: string[] = ctx.viewport.getImageIds?.() ?? [];
  const sliceIndex = allImageIds.indexOf(imageId);

  const uid =
    cornerstoneOrUtils.uuidv4?.() ?? `rg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const annotation = {
    annotationUID: uid,
    metadata: {
      toolName: "PlanarFreehandContourSegmentationTool",
      referencedImageId: imageId,
      FrameOfReferenceUID: ctx.viewport.getFrameOfReferenceUID?.(),
      viewPlaneNormal: [...viewPlaneNormal],
      viewUp: [...viewUp],
      sliceIndex,
    },
    data: {
      contour: { polyline: worldPoints, closed: true },
      segmentation: { segmentationId: segId, segmentIndex: _segmentIndex },
      handles: {
        points: [],
        activeHandleIndex: null,
        textBox: {
          hasMoved: false,
          worldPosition: [0, 0, 0],
          worldBoundingBox: {
            topLeft: [0, 0, 0],
            topRight: [0, 0, 0],
            bottomLeft: [0, 0, 0],
            bottomRight: [0, 0, 0],
          },
        },
      },
    },
    highlighted: false,
    isLocked: false,
    isVisible: true,
    invalidated: false,
    _regionGrow: true, // Skip cleanupPolyline — contour is already clean
  };

  return { annotation, uid };
}

/** Convert smoothed image-space points to world coordinates. Prefers the
 *  viewport's clicked imageId so the seed contour lands exactly on the
 *  user's slice regardless of plane (axial / coronal / sagittal primary).
 *  Falls back to indexToWorld([c, r, 0]) for callers that don't have the
 *  imageId — those callers were also broken on non-axial planes, but at
 *  least they preserve prior behavior. */
function toWorldPoints(
  smoothed: Pt2[],
  vtkImageData: any,
  ctx?: { viewport?: any; imageId?: string; sliceIndex?: number },
): Pt3[] | null {
  if (!vtkImageData) return null;

  // Fast path: build world points from the clicked imageId's IPP + IOP.
  // This is what propagateToAdjacentSlices uses (`pixelsToWorld` below at
  // line ~1061) — using the same math here keeps the seed slice's contour
  // aligned with the user's clicked slice.
  const imageId = ctx?.imageId;
  if (imageId) {
    try {
      const cs = getCornerstone();
      const ipm = cs.metaData.get("imagePlaneModule", imageId);
      const ipp = ipm?.imagePositionPatient;
      const rowCos = ipm?.rowCosines ?? [1, 0, 0];
      const colCos = ipm?.columnCosines ?? [0, 1, 0];
      const cSp = ipm?.columnPixelSpacing ?? 1;
      const rSp = ipm?.rowPixelSpacing ?? 1;
      if (ipp) {
        return smoothed.map(
          ([r, c]) =>
            [
              ipp[0] + c * cSp * rowCos[0] + r * rSp * colCos[0],
              ipp[1] + c * cSp * rowCos[1] + r * rSp * colCos[1],
              ipp[2] + c * cSp * rowCos[2] + r * rSp * colCos[2],
            ] as Pt3,
        );
      }
    } catch (err) {
      logger.warn(
        `[RegionGrowTool] toWorldPoints IPP path failed for imageId=${imageId}; falling back to indexToWorld:`,
        err,
      );
    }
  }

  const k = ctx?.sliceIndex ?? 0;
  return smoothed.map(([r, c]) => {
    const wp = vtkImageData.indexToWorld([c, r, k]);
    return [wp[0], wp[1], wp[2]] as Pt3;
  });
}

// ─── HU reading ──────────────────────────────────────────────────────────────

function readHUAtCanvas(
  viewport: any,
  canvasPoint: Pt2,
  csUtils: any,
): {
  hu: number;
  row: number;
  col: number;
  dimensions: [number, number];
  scalarData: any;
  imageData: any;
} | null {
  const imageData = viewport.getImageData?.();
  if (!imageData) return null;

  // On VolumeViewport the imageData proxy's `scalarData` getter calls into
  // VoxelManager and throws "No scalar data available" when the volume's
  // pixel data isn't held as one flat typed array (progressive HTJ2K backend).
  // Pull the active slice's pixels directly via VoxelManager instead — the
  // tool is 2D anyway (snap/flood-fill index as row*cols+col).
  const {
    dimensions,
    imageData: vtkImageData,
    voxelManager,
  } = imageData as {
    dimensions: number[];
    imageData: any;
    voxelManager?: {
      getSliceData?: (args: { sliceIndex: number; slicePlane: number }) => ArrayLike<number>;
    };
  };
  if (!vtkImageData || !dimensions || dimensions.length < 2) return null;

  const cols = dimensions[0];
  const rows = dimensions[1];
  const worldPoint: Pt3 = viewport.canvasToWorld(canvasPoint);
  const indexPoint = csUtils.transformWorldToIndex(vtkImageData, worldPoint);
  if (!indexPoint) return null;

  const col = indexPoint[0];
  const row = indexPoint[1];
  const sliceIndex = Math.round(indexPoint[2] ?? 0);
  if (col < 0 || col >= cols || row < 0 || row >= rows) return null;

  let scalarData: ArrayLike<number> | null = null;
  try {
    scalarData = voxelManager?.getSliceData?.({ sliceIndex, slicePlane: 2 }) ?? null;
  } catch (e) {
    logger.debug("[RegionGrowTool] voxelManager.getSliceData failed", { sliceIndex, error: e });
    return null;
  }
  if (!scalarData || scalarData.length < cols * rows) return null;

  // The canonicalized HTJ2K pipeline returns CT pixel data as Uint16Array
  // even though the underlying values are signed int16 (HU offsets included).
  // Positive HU values read identically through both views, but negative HU
  // (air -1000, fat -80, etc.) come back as huge positives (-1000 → 64536).
  // adaptiveMinHU's background ring averages those into a bogus ~23000+
  // bgMean, which inverts the FWHM range and rejects the seed. Reinterpret
  // as Int16Array — same bytes, signed view.
  if (scalarData instanceof Uint16Array) {
    scalarData = new Int16Array(scalarData.buffer, scalarData.byteOffset, scalarData.length);
  }

  // Snap to brightest pixel via valley-aware BFS (prevents jumping to adjacent stones).
  // Scale search radius with zoom: when zoomed out, the click covers more image pixels.
  const zoom = viewport.getZoom?.() ?? 1;
  const SNAP_RADIUS = Math.max(5, Math.round(10 / Math.max(zoom, 0.1)));
  const snapped = snapToBrightest(scalarData, cols, rows, row, col, SNAP_RADIUS);
  return {
    hu: snapped.hu,
    row: snapped.row,
    col: snapped.col,
    dimensions: [cols, rows],
    scalarData,
    imageData,
  };
}

// ─── Flood-fill ──────────────────────────────────────────────────────────────
// floodFill2D moved to regionGrowHelpers.ts — re-exported above for tests.

/**
 * Multi-seed fill: union of fills from all seed points, minus exclusion fills.
 */
export function multiSeedFill(
  scalarData: any,
  cols: number,
  rows: number,
  seeds: Pt2[],
  exclusions: Pt2[],
  minHU: number,
  maxHU: number,
  maxRadiusPx: number,
): Uint8Array | null {
  const combined = new Uint8Array(cols * rows);
  let totalFilled = 0;

  // Union all seed fills
  for (const [sr, sc] of seeds) {
    const m = floodFill2D(scalarData, cols, rows, sr, sc, minHU, maxHU, maxRadiusPx);
    if (m) {
      for (let i = 0; i < m.length; i++) {
        if (m[i] && !combined[i]) {
          combined[i] = 1;
          totalFilled++;
        }
      }
    }
  }

  // Subtract exclusion fills
  for (const [er, ec] of exclusions) {
    const m = floodFill2D(scalarData, cols, rows, er, ec, minHU, maxHU, maxRadiusPx);
    if (m) {
      for (let i = 0; i < m.length; i++) {
        if (m[i] && combined[i]) {
          combined[i] = 0;
          totalFilled--;
        }
      }
    }
  }

  return totalFilled >= 2 ? combined : null;
}

// ─── Hole filling ────────────────────────────────────────────────────────────

export function fillHoles(mask: Uint8Array, cols: number, rows: number): void {
  const bg = new Uint8Array(cols * rows);
  const queue: number[] = [];

  for (let c = 0; c < cols; c++) {
    if (!mask[c]) {
      bg[c] = 1;
      queue.push(c);
    }
    const bIdx = (rows - 1) * cols + c;
    if (!mask[bIdx]) {
      bg[bIdx] = 1;
      queue.push(bIdx);
    }
  }
  for (let r = 1; r < rows - 1; r++) {
    const lIdx = r * cols;
    if (!mask[lIdx]) {
      bg[lIdx] = 1;
      queue.push(lIdx);
    }
    const rIdx = r * cols + (cols - 1);
    if (!mask[rIdx]) {
      bg[rIdx] = 1;
      queue.push(rIdx);
    }
  }

  const dr = [-1, 1, 0, 0],
    dc = [0, 0, -1, 1];
  let head = 0;
  while (head < queue.length) {
    const idx = queue[head++];
    const r = Math.floor(idx / cols),
      c = idx % cols;
    for (let i = 0; i < 4; i++) {
      const nr = r + dr[i],
        nc = c + dc[i];
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      const nIdx = nr * cols + nc;
      if (bg[nIdx] || mask[nIdx]) continue;
      bg[nIdx] = 1;
      queue.push(nIdx);
    }
  }

  for (let i = 0; i < mask.length; i++) {
    if (!mask[i] && !bg[i]) mask[i] = 1;
  }
}

/**
 * Outlines of the fill, traced along pixel edges (ENG-250), largest first.
 * Each rasterizes back to exactly its blob's pixels; a 1-pixel blob gives a
 * 4-point square.
 *
 * Traced 8-connected to match `floodFill2D`, so pixels the fill reached through
 * a corner stay in the stone's outline. A single seed always gives one
 * outline; several Shift+Click seeds can give separate blobs.
 */
export function maskOutlines(mask: Uint8Array, cols: number, rows: number): Pt2[][] {
  const area = (outline: Pt2[]) => {
    let twiceArea = 0;
    for (let i = 0; i < outline.length; i++) {
      const [r1, c1] = outline[i];
      const [r2, c2] = outline[(i + 1) % outline.length];
      twiceArea += c1 * r2 - c2 * r1;
    }
    return Math.abs(twiceArea) / 2;
  };
  return extractContourEdges(mask, cols, rows, 8)
    .map((outline) => ({ outline, area: area(outline) }))
    .sort((a, b) => b.area - a.area)
    .map(({ outline }) => outline);
}

// ─── Contour resampling ──────────────────────────────────────────────────────

/** Target point count for all region grow contours. Consistent count across
 *  slices helps Cornerstone's interpolation produce clean results. */
const RESAMPLE_TARGET = 200;

/**
 * Resample a closed contour to a fixed number of evenly-spaced points.
 * Walks the perimeter at uniform arc-length intervals.
 */
function resampleContour(points: Pt2[], targetCount: number = RESAMPLE_TARGET): Pt2[] {
  const n = points.length;
  if (n <= 3 || n <= targetCount) return points;

  // Compute cumulative arc lengths
  const cumLen: number[] = [0];
  for (let i = 1; i <= n; i++) {
    const [r1, c1] = points[i - 1];
    const [r2, c2] = points[i % n];
    cumLen.push(cumLen[i - 1] + Math.sqrt((r2 - r1) ** 2 + (c2 - c1) ** 2));
  }
  const totalLen = cumLen[n];
  if (totalLen === 0) return points;

  const step = totalLen / targetCount;
  const resampled: Pt2[] = [];
  let segIdx = 0;

  for (let i = 0; i < targetCount; i++) {
    const targetDist = i * step;

    // Advance to the segment containing targetDist
    while (segIdx < n - 1 && cumLen[segIdx + 1] < targetDist) {
      segIdx++;
    }

    const segStart = cumLen[segIdx];
    const segEnd = cumLen[segIdx + 1];
    const segLen = segEnd - segStart;
    const t = segLen > 0 ? (targetDist - segStart) / segLen : 0;

    const [r1, c1] = points[segIdx];
    const [r2, c2] = points[(segIdx + 1) % n];
    resampled.push([r1 + t * (r2 - r1), c1 + t * (c2 - c1)]);
  }

  // Rotate so the contour starts from the topmost point (minimum row).
  // This ensures consistent starting position across slices, which is
  // critical for Cornerstone's interpolation — it matches points by index,
  // so misaligned starts produce crossing/collapsed interpolated contours.
  let topIdx = 0;
  let topRow = resampled[0][0];
  for (let i = 1; i < resampled.length; i++) {
    if (resampled[i][0] < topRow) {
      topRow = resampled[i][0];
      topIdx = i;
    }
  }
  if (topIdx > 0) {
    const rotated = resampled.slice(topIdx).concat(resampled.slice(0, topIdx));
    return rotated;
  }

  return resampled;
}

// ─── Core fill→contour pipeline ──────────────────────────────────────────────
// adaptiveMinHU moved to regionGrowHelpers.ts — re-exported above for tests.

/** Minimum pixel count to consider a fill "too small" and retry with adaptive threshold */
const ADAPTIVE_RETRY_THRESHOLD = 75;

/** Contours of the clicked slice's fill, largest first (one per blob). */
function runFillPipeline(ctx: NonNullable<typeof _cachedContext>): Pt2[][] | null {
  const { scalarData, cols, rows, effectiveMin, effectiveMax, maxRadiusPx, smoothingIter } = ctx;
  let mask =
    _seeds.length === 1 && _exclusions.length === 0
      ? floodFill2D(
          scalarData,
          cols,
          rows,
          _seeds[0][0],
          _seeds[0][1],
          effectiveMin,
          effectiveMax,
          maxRadiusPx,
        )
      : multiSeedFill(
          scalarData,
          cols,
          rows,
          _seeds,
          _exclusions,
          effectiveMin,
          effectiveMax,
          maxRadiusPx,
        );

  // If the fill is too small, retry with an adaptive threshold
  // This handles small structures where partial volume effects push edge pixels below hu_range
  if (mask && _seeds.length === 1) {
    let fillCount = 0;
    for (let i = 0; i < mask.length; i++) if (mask[i]) fillCount++;

    if (fillCount < ADAPTIVE_RETRY_THRESHOLD) {
      const [sr, sc] = _seeds[0];
      const seedHU = scalarData[sr * cols + sc] as number;
      const adaptiveMin = adaptiveMinHU(scalarData, cols, rows, sr, sc, seedHU);
      // Only retry if adaptive threshold is lower than the configured minimum
      if (adaptiveMin < effectiveMin) {
        const retryMask = floodFill2D(
          scalarData,
          cols,
          rows,
          sr,
          sc,
          adaptiveMin,
          effectiveMax,
          maxRadiusPx,
        );
        if (retryMask) {
          let retryCount = 0;
          for (let i = 0; i < retryMask.length; i++) if (retryMask[i]) retryCount++;
          if (retryCount > fillCount) {
            mask = retryMask;
          }
        }
      }
    }
  }

  if (!mask) return null;

  fillHoles(mask, cols, rows);
  const outlines = maskOutlines(mask, cols, rows);
  return outlines.length ? outlines.map((o) => smoothDisplayedContour(o, smoothingIter)) : null;
}

/**
 * Smooth + resample the clicked slice's outline. Exported for tests: with an
 * edge outline the result still rasterizes to exactly the outline's pixels.
 */
export function smoothDisplayedContour(outline: Pt2[], smoothingIter: number): Pt2[] {
  // Always smooth at least once to round off sharp pixel corners.
  // Small contours get fewer iterations to avoid inflating beyond the fill area.
  const effectiveSmoothing =
    outline.length < 10
      ? 1
      : outline.length < 30
        ? Math.max(Math.min(smoothingIter, 2), 1)
        : smoothingIter;

  const smoothed = effectiveSmoothing > 0 ? chaikinSmooth(outline, effectiveSmoothing) : outline;

  // Always resample to consistent point count for interpolation compatibility.
  // Cornerstone's interpolation matches points by index — inconsistent counts
  // between keyframes cause collapsed/crossed interpolated contours.
  // Only skip resampling for very small contours (<30 pts) where resampling
  // would inflate them.
  if (smoothed.length >= 30) {
    return resampleContour(smoothed);
  }
  return smoothed;
}

// ─── Auto-propagation across slices ─────────────────────────────────────────

/** Compute the centroid (mean row, mean col) of a contour. */
function contourCentroid(points: Pt2[]): Pt2 {
  let sumR = 0,
    sumC = 0;
  for (const [r, c] of points) {
    sumR += r;
    sumC += c;
  }
  return [Math.round(sumR / points.length), Math.round(sumC / points.length)];
}

/** Default HU floor for the valley barrier in `snapToBrightestBfs`. Matches
 *  the FWHM floor used by the region-grow pipeline — values below this look
 *  like soft tissue and shouldn't be traversed during snapping. */
export const DEFAULT_VALLEY_THRESHOLD_HU = 130;

/**
 * Pure helper: snap a seed point to the brightest pixel within a search
 * radius using BFS with a valley barrier. Exported (and parameter-driven)
 * so tests can exercise the production algorithm directly without the
 * runtime `_config` singleton.
 */
export function snapToBrightestBfs(
  scalarData: ArrayLike<number>,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  radius: number,
  valleyThreshold: number = DEFAULT_VALLEY_THRESHOLD_HU,
): { row: number; col: number; hu: number } {
  let bestRow = seedRow,
    bestCol = seedCol;
  let bestHU = scalarData[seedRow * cols + seedCol] as number;
  const rSq = radius * radius;

  const visited = new Uint8Array(cols * rows);
  const queue: [number, number][] = [[seedRow, seedCol]];
  let qHead = 0;
  visited[seedRow * cols + seedCol] = 1;

  while (qHead < queue.length) {
    const [cr, cc] = queue[qHead++];
    const hu = scalarData[cr * cols + cc] as number;
    if (hu > bestHU) {
      bestHU = hu;
      bestRow = cr;
      bestCol = cc;
    }
    for (let i = 0; i < 4; i++) {
      const nr = cr + (i === 0 ? -1 : i === 1 ? 1 : 0);
      const nc = cc + (i === 2 ? -1 : i === 3 ? 1 : 0);
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if ((nr - seedRow) ** 2 + (nc - seedCol) ** 2 > rSq) continue;
      const nIdx = nr * cols + nc;
      if (visited[nIdx]) continue;
      visited[nIdx] = 1;
      if ((scalarData[nIdx] as number) < valleyThreshold) continue;
      queue.push([nr, nc]);
    }
  }
  return { row: bestRow, col: bestCol, hu: bestHU };
}

/**
 * Convenience wrapper that pulls `valleyThreshold` from the active region-grow
 * config — keeps existing call sites unchanged while exposing the pure
 * variant above for tests.
 */
export function snapToBrightest(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  radius: number,
): { row: number; col: number; hu: number } {
  const valleyThreshold = _config?.fwhm_floor ?? DEFAULT_VALLEY_THRESHOLD_HU;
  return snapToBrightestBfs(scalarData, cols, rows, seedRow, seedCol, radius, valleyThreshold);
}

/**
 * Run the fill pipeline on an arbitrary slice's pixel data (not the displayed slice).
 * Uses the same algorithm as runFillPipeline but with externally-provided scalarData
 * and a single seed point (the projected centroid, snapped to brightest nearby pixel).
 */
function runFillPipelineOnSlice(
  scalarData: any,
  cols: number,
  rows: number,
  seedRow: number,
  seedCol: number,
  effectiveMin: number,
  effectiveMax: number,
  maxRadiusPx: number,
  smoothingIter: number,
): Pt2[] | null {
  // Clamp seed to image bounds
  if (seedRow < 0 || seedRow >= rows || seedCol < 0 || seedCol >= cols) return null;

  // Snap to brightest pixel in a 10px radius around the centroid.
  // The centroid from the previous slice may land on soft tissue even if the
  // stone is just a few pixels away on this slice.
  const snapped = snapToBrightest(scalarData, cols, rows, seedRow, seedCol, 10);
  if (snapped.hu < effectiveMin) return null;

  let mask = floodFill2D(
    scalarData,
    cols,
    rows,
    snapped.row,
    snapped.col,
    effectiveMin,
    effectiveMax,
    maxRadiusPx,
  );
  if (!mask) return null;

  // Adaptive threshold retry — same logic as runFillPipeline.
  // If fill is small (<50 px), sample background HU and retry with a lower
  // threshold to capture partial-volume edge pixels.
  let fillCount = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i]) fillCount++;
  if (fillCount < ADAPTIVE_RETRY_THRESHOLD) {
    const seedHU = scalarData[snapped.row * cols + snapped.col] as number;
    const adaptiveMin = adaptiveMinHU(scalarData, cols, rows, snapped.row, snapped.col, seedHU);
    if (adaptiveMin < effectiveMin) {
      const retryMask = floodFill2D(
        scalarData,
        cols,
        rows,
        snapped.row,
        snapped.col,
        adaptiveMin,
        effectiveMax,
        maxRadiusPx,
      );
      if (retryMask) {
        let retryCount = 0;
        for (let i = 0; i < retryMask.length; i++) if (retryMask[i]) retryCount++;
        if (retryCount > fillCount) {
          mask = retryMask;
        }
      }
    }
  }

  fillHoles(mask, cols, rows);
  // One seed: the 8-connected fill is a single blob, so a single outline.
  const [outline] = maskOutlines(mask, cols, rows);
  return outline ? smoothPropagatedContour(outline, smoothingIter) : null;
}

/**
 * Smooth, upsample and resample a propagated slice's outline to
 * RESAMPLE_TARGET points. Exported for tests: with an edge outline the result
 * still rasterizes to exactly the outline's pixels.
 */
export function smoothPropagatedContour(outline: Pt2[], smoothingIter: number): Pt2[] {
  const effectiveSmoothing =
    outline.length < 10 ? 0 : outline.length < 30 ? Math.min(smoothingIter, 1) : smoothingIter;

  const smoothed = effectiveSmoothing > 0 ? chaikinSmooth(outline, effectiveSmoothing) : outline;

  // Ensure enough points for resampling. resampleContour requires input > targetCount
  // to actually resample. For tiny contours, apply extra Chaikin rounds to upsample.
  // Each round roughly doubles point count: 6 → 12 → 24 → 48 → 96 → 192 → 384
  let upsampled = smoothed;
  while (upsampled.length <= RESAMPLE_TARGET) {
    upsampled = chaikinSmooth(upsampled, 1);
  }
  return resampleContour(upsampled);
}

// ─── Tool factory ────────────────────────────────────────────────────────────

export function createRegionGrowToolClass(cornerstone: any, cornerstoneTools: any): any {
  const { BaseTool } = cornerstoneTools;
  const { getEnabledElement, utilities: csUtils } = cornerstone;

  /** Remove the current preview annotation if one exists. */
  function removePreview(): void {
    if (!_previewUID) return;
    removePreviewAnnotations(cornerstoneTools);
    _previewUID = null;
    _previewExtraUIDs = [];
    _previewImageId = null;
  }

  // Initialize nnInteractive session module with Cornerstone refs
  initializeNnSession(cornerstone, cornerstoneTools, csUtils);

  /** Capture before seed/context changes; publish only after a contour exists. */
  function beginThresholdUndo(viewport: any) {
    const scope = getActiveScope();
    if (!getDefaultStore().get(isSegmentationActiveAtom) || !scope) return undefined;
    const viewports = new Set<any>([viewport]);
    const siblings = getSiblingViewports();
    for (const plane of PLANE_KEYS) {
      if (siblings?.[plane]?.viewport) viewports.add(siblings[plane]!.viewport);
    }
    const geometry = [...viewports].map((vp) => ({
      viewport: vp,
      checkpoint: captureGeometryForViewport(vp, _segmentIndex),
    }));
    const restoreNn = captureNnStateForThresholdUndo();
    const before = {
      previewUID: _previewUID,
      previewExtraUIDs: [..._previewExtraUIDs],
      previewImageId: _previewImageId,
      seeds: _seeds.map((p) => [...p] as Pt2),
      exclusions: _exclusions.map((p) => [...p] as Pt2),
      context: _cachedContext,
      propagatedUIDs: [..._propagatedUIDs],
      fallback: Array.from(
        _fallbackPerPlane,
        ([plane, entry]) =>
          [plane, { ...entry, propagatedUIDs: [...entry.propagatedUIDs] }] as [
            PlaneKey,
            FallbackPlaneEntry,
          ],
      ),
      primaryPlane: _fallbackPrimaryPlane,
      imageIds: [..._regionGrowImageIds],
    };
    // A newer grow also stops the previous grow's unfinished slice loading.
    _thresholdGeneration += 1;
    bumpFallbackCompanionToken();
    const generation = _thresholdGeneration;
    const isCurrent = () => {
      const current = getActiveScope();
      return (
        generation === _thresholdGeneration &&
        current?.session === scope.session &&
        current.studyUid === scope.studyUid
      );
    };
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      _thresholdGeneration += 1;
      bumpFallbackCompanionToken();
      for (const { viewport: vp, checkpoint } of geometry) {
        restoreGeometryCheckpoint(vp, checkpoint);
      }
      _previewUID = before.previewUID;
      _previewExtraUIDs = [...before.previewExtraUIDs];
      _previewImageId = before.previewImageId;
      _seeds.splice(0, _seeds.length, ...before.seeds);
      _exclusions.splice(0, _exclusions.length, ...before.exclusions);
      _cachedContext = before.context;
      _setCachedContext(_cachedContext);
      _propagatedUIDs = [...before.propagatedUIDs];
      _fallbackPerPlane.clear();
      for (const [plane, entry] of before.fallback) _fallbackPerPlane.set(plane, entry);
      _fallbackPrimaryPlane = before.primaryPlane;
      _regionGrowImageIds.clear();
      for (const id of before.imageIds) _regionGrowImageIds.add(id);
      restoreNn();
      for (const vp of viewports) {
        vp.render?.();
        cornerstoneTools.utilities.triggerAnnotationRender(vp.element);
      }
    };
    return {
      isCurrent,
      restore,
      commit: () => {
        if (isCurrent()) pushUndo({ kind: "thresholdRevert", undo: restore });
      },
    };
  }

  /** Create or update the preview annotation from the current seeds/exclusions. */
  function applyThresholdPreview(
    transaction: ReturnType<typeof beginThresholdUndo>,
    multiSeed = false,
  ): boolean {
    try {
      if (updatePreview(multiSeed)) {
        transaction?.commit();
        return true;
      }
    } catch (err) {
      if (!transaction) throw err;
      logger.warn("[RegionGrowTool] threshold contour failed:", err);
    }
    // Preserve the legacy caller flow outside the segmentation Undo scope.
    if (!transaction) return true;
    transaction?.restore();
    return false;
  }

  function updatePreview(isMultiSeedUpdate: boolean = false): boolean {
    if (!_cachedContext || _seeds.length === 0) return false;
    const ctx = _cachedContext;

    // One contour per blob, largest first: several Shift+Click seeds can grow
    // apart, and every blob is part of the stone (ENG-250).
    const contours = runFillPipeline(ctx);
    if (!contours) {
      // Fill produced no contour (seed outside HU range, or fill too small)
      return false;
    }
    const smoothed = contours[0];

    // Remove previous annotation only if it's on the same slice (replacement).
    // Different-slice annotations are already committed and should persist.
    const currentImageId = ctx.viewport.getCurrentImageId?.();
    const worldContours: Pt3[][] = [];
    for (const contour of contours) {
      const worldPoints = toWorldPoints(contour, ctx.imageData.imageData, {
        viewport: ctx.viewport,
        imageId: currentImageId ?? undefined,
      });
      if (!worldPoints) return false;
      worldContours.push(worldPoints);
    }

    if (_previewUID && _previewImageId === currentImageId) removePreview();

    const addPreviewAnnotation = (worldPoints: Pt3[]) => {
      const { annotation, uid } = buildContourAnnotation(
        worldPoints,
        ctx,
        csUtils,
        cornerstoneTools,
      );

      // Mark as region-grow but NOT autoGenerated — the seed click is a
      // user-initiated keyframe and should render as a solid line. Only
      // propagated slices (in propagateToAdjacentSlices) are autoGenerated.
      // The _regionGrow flag lets InterpolationManager skip these contours
      // without making them appear as predicted/dashed.
      (annotation as any)._regionGrow = true;

      cornerstoneTools.annotation.state.addAnnotation(annotation, ctx.element);

      // Register with segmentation contour representation so the renderer draws it.
      // Without this, the annotation exists in state but is invisible — the
      // segmentation pipeline only renders annotations in annotationUIDsMap.
      try {
        cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(annotation);
      } catch (err) {
        // "No active segmentation" means the viewport loaded slowly and segmentation
        // setup hasn't completed yet. Try to create one on the fly so the radiologist's
        // click isn't lost.
        if (String(err).includes("No active segmentation")) {
          console.warn(
            "[RegionGrowTool] No active segmentation — attempting on-demand creation...",
          );
          try {
            const { segmentation, Enums: ToolEnums } = cornerstoneTools;
            const vpId = ctx.viewport.id;
            const segId = `${vpId}_ondemand_segmentation`;
            try {
              segmentation.removeSegmentation(segId);
            } catch (e) {
              logger.debug("[RegionGrowTool] removeSegmentation skipped:", e);
            }
            segmentation.addSegmentations([
              {
                segmentationId: segId,
                representation: { type: ToolEnums.SegmentationRepresentations.Contour },
              },
            ]);
            segmentation.addSegmentationRepresentations(vpId, [
              {
                segmentationId: segId,
                type: ToolEnums.SegmentationRepresentations.Contour,
              },
            ]);
            segmentation.activeSegmentation.setActiveSegmentation(vpId, segId);
            // Retry registration
            cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(
              annotation,
            );
            logger.debug("[RegionGrowTool] On-demand segmentation created, contour registered");
          } catch (retryErr) {
            if (getDefaultStore().get(isSegmentationActiveAtom)) throw retryErr;
            console.error(
              "[RegionGrowTool] On-demand segmentation creation also failed:",
              retryErr,
            );
          }
        } else {
          if (getDefaultStore().get(isSegmentationActiveAtom)) throw err;
          console.error(
            "[RegionGrowTool] addContourSegmentationAnnotation failed — contour will be invisible:",
            err,
          );
        }
      }
      return { annotation, uid };
    };
    const added = worldContours.map(addPreviewAnnotation);
    const annotations = added.map((a) => a.annotation);

    // Track for undo — the largest blob's UID, plus the other blobs'
    _previewUID = added[0].uid;
    _previewExtraUIDs = added.slice(1).map((a) => a.uid);
    _previewImageId = ctx.viewport.getCurrentImageId?.();

    // Render immediately so the first click appears instantly.
    try {
      ctx.viewport.render?.();
      cornerstoneTools.utilities.triggerAnnotationRender(ctx.element);
      // Notify SegmentationStatsOverlay to recompute HU metrics.
      // Omit annotation from detail so InterpolationManager ignores this event —
      // region-grow contours use auto-propagation, not geo-interpolation.
      cornerstone.eventTarget?.dispatchEvent(
        new CustomEvent("CORNERSTONE_TOOLS_ANNOTATION_MODIFIED", { detail: {} }),
      );
    } catch (e) {
      logger.debug("[RegionGrowTool] post-commit render skipped:", e);
    }

    // Notify VolumeViewport to set drawing state (isDrawing, hasFirstStroke,
    // lockedSeriesId) which enables HU stats overlay and Save button.
    // Use the direct callback instead of triggerAnnotationCompleted to avoid
    // invoking InterpolationManager which creates duplicate contours.
    const ctxViewportId: string | null = ctx.viewport?.id ?? null;
    const generation = _thresholdGeneration;
    const undoScope = getDefaultStore().get(isSegmentationActiveAtom) ? getActiveScope() : null;
    const isCurrent = () =>
      generation === _thresholdGeneration &&
      (!undoScope || getActiveScope()?.session === undoScope.session);
    fireRegionGrowCommit(ctxViewportId, currentImageId ?? null);

    // Auto-switch to bump tool after initial contour placement (not multi-seed updates).
    // Use setTimeout(0) so the switch happens after Cornerstone's full event pipeline
    // completes — queueMicrotask fires before InterpolationManager finishes.
    if (!isMultiSeedUpdate) {
      setTimeout(() => {
        if (isCurrent()) fireRegionGrowAutoSwitch(ctxViewportId);
      }, 0);
    }

    // Auto-propagate after a short delay so the browser can paint the initial contour.
    // Only a plain click propagates, and it has a single seed, so the fill is
    // one blob and `smoothed` (contours[0]) is all of it. Shift/Ctrl+Click
    // refinements do not re-propagate.
    if (!isMultiSeedUpdate && _config?.auto_propagate) {
      setTimeout(() => {
        if (!isCurrent()) return;
        propagateToAdjacentSlices(smoothed, isCurrent).catch((err) => {
          console.warn("[RegionGrowTool] Auto-propagation failed:", err);
        });
      }, 16);
    } else if (!_config?.auto_propagate) {
      // No auto-propagation — fire ANNOTATION_COMPLETED so InterpolationManager
      // can generate interpolated contours between manual keyframes.
      setTimeout(() => {
        if (!isCurrent()) return;
        for (const annotation of annotations) {
          try {
            cornerstoneTools.annotation.state.triggerAnnotationCompleted(annotation);
          } catch (err) {
            console.error("[RegionGrowTool] triggerAnnotationCompleted failed:", err);
          }
        }
      }, 0);
    }
    return true;
  }

  /**
   * Auto-propagate region grow to adjacent slices using centroid chaining.
   *
   * After a region grow click creates a contour on the current slice, this
   * function propagates outward in both directions (+1, -1 slice index).
   * On each adjacent slice it:
   *   1. Loads the slice's pixel data from the image cache
   *   2. Seeds at the centroid of the previous slice's contour
   *   3. Runs the fill pipeline with the same HU thresholds
   *   4. Creates an annotation and commits it
   *   5. Repeats with the new contour's centroid
   * Stops when the seed HU falls below threshold or the fill fails.
   * Skips slices that already have a contour for this segment.
   */
  async function propagateToAdjacentSlices(
    initialContour: Pt2[],
    isCurrent: () => boolean,
  ): Promise<void> {
    if (!_cachedContext) return;
    const ctx = _cachedContext;
    const { viewport, element, effectiveMin, effectiveMax, maxRadiusPx, smoothingIter } = ctx;

    const allImageIds: string[] = viewport.getImageIds?.() ?? [];
    const currentImageId = viewport.getCurrentImageId?.();
    const currentIndex = allImageIds.indexOf(currentImageId);
    if (currentIndex < 0 || allImageIds.length <= 1) return;

    logger.debug(
      `[RegionGrowTool] Auto-propagating from slice ${currentIndex} (${allImageIds.length} total)`,
    );

    // Check which slices already have annotations for this segment
    const allAnns: any[] = cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
    const annotatedImageIds = new Set<string>();
    for (const ann of allAnns) {
      if (
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        ann.data?.segmentation?.segmentIndex === _segmentIndex &&
        ann.metadata?.referencedImageId
      ) {
        annotatedImageIds.add(ann.metadata.referencedImageId);
      }
    }

    // Use a local array to avoid race conditions if a second click fires while
    // the first propagation is still awaiting image loads.
    const localUIDs: string[] = [];
    _propagatedUIDs = localUIDs;

    // Get segmentation info and camera from the seed slice (shared across all propagated)
    const activeSeg = cornerstoneTools.segmentation.getActiveSegmentation?.(viewport.id);
    const segId =
      activeSeg?.segmentationId ??
      cornerstoneTools.segmentation.state.getSegmentations()?.[0]?.segmentationId ??
      "unknown";
    const camera = viewport.getCamera?.() ?? {};
    const viewPlaneNormal = camera.viewPlaneNormal ?? [0, 0, 1];
    const viewUp = camera.viewUp ?? [0, -1, 0];
    const forUID = viewport.getFrameOfReferenceUID?.();

    /** Convert [row, col] pixel points to world coords using imagePlaneModule metadata. */
    function pixelsToWorld(points: Pt2[], ipm: any, image: any): Pt3[] | null {
      const ipp = ipm.imagePositionPatient;
      if (!ipp) return null;
      const rowCos = ipm.rowCosines ?? [1, 0, 0];
      const colCos = ipm.columnCosines ?? [0, 1, 0];
      const cSp = ipm.columnPixelSpacing ?? image.columnPixelSpacing ?? 1;
      const rSp = ipm.rowPixelSpacing ?? image.rowPixelSpacing ?? 1;
      return points.map(
        ([r, c]) =>
          [
            ipp[0] + c * cSp * rowCos[0] + r * rSp * colCos[0],
            ipp[1] + c * cSp * rowCos[1] + r * rSp * colCos[1],
            ipp[2] + c * cSp * rowCos[2] + r * rSp * colCos[2],
          ] as Pt3,
      );
    }

    /** Propagate in one direction (delta = +1 or -1). No viewport navigation. */
    async function propagateDirection(delta: number, startContour: Pt2[]): Promise<void> {
      let prevContour = startContour;

      for (let idx = currentIndex + delta; idx >= 0 && idx < allImageIds.length; idx += delta) {
        // Cancel if a newer propagation has taken over
        if (!isCurrent() || localUIDs !== _propagatedUIDs) return;

        const targetImageId = allImageIds[idx];

        // Stop at slices that already have annotations for this segment
        // (don't jump over them — keeps propagation chains consistent)
        if (annotatedImageIds.has(targetImageId)) break;

        // Load the target slice's pixel data from cache
        let targetImage: any;
        try {
          targetImage = await cornerstone.imageLoader.loadAndCacheImage(targetImageId);
        } catch (e) {
          logger.debug("[RegionGrowTool] loadAndCacheImage failed, stopping propagation:", e);
          break;
        }
        if (!isCurrent() || localUIDs !== _propagatedUIDs) return;
        let rawPixelData = targetImage.getPixelData?.();
        const targetCols = targetImage.columns;
        const targetRows = targetImage.rows;
        if (!rawPixelData || !targetCols || !targetRows) break;

        // Reinterpret Uint16 → Int16 so negative HU (air/fat/lung) doesn't
        // wrap to 65000+. Same fix as readHUAtCanvas + companion seed path.
        if (rawPixelData instanceof Uint16Array) {
          rawPixelData = new Int16Array(
            rawPixelData.buffer,
            rawPixelData.byteOffset,
            rawPixelData.length,
          );
        }

        // Convert raw pixel data to HU if needed
        const slope = targetImage.slope ?? 1;
        const intercept = targetImage.intercept ?? 0;
        const isPreScaled = !!targetImage.preScale;
        let huData: any;
        if (isPreScaled || (slope === 1 && intercept === 0)) {
          huData = rawPixelData;
        } else {
          huData = new Float32Array(rawPixelData.length);
          for (let i = 0; i < rawPixelData.length; i++) {
            huData[i] = rawPixelData[i] * slope + intercept;
          }
        }

        // Compute seed: centroid of previous slice's contour, snapped to brightest
        const [seedRow, seedCol] = contourCentroid(prevContour);
        if (seedRow < 0 || seedRow >= targetRows || seedCol < 0 || seedCol >= targetCols) break;

        const snapped = snapToBrightest(huData, targetCols, targetRows, seedRow, seedCol, 10);
        if (snapped.hu < effectiveMin) {
          logger.debug(
            `[RegionGrowTool] Propagation stopped at slice ${idx}: snappedHU=${snapped.hu.toFixed(0)} < ${effectiveMin.toFixed(0)}`,
          );
          break;
        }

        // Run fill pipeline
        const smoothed = runFillPipelineOnSlice(
          huData,
          targetCols,
          targetRows,
          snapped.row,
          snapped.col,
          effectiveMin,
          effectiveMax,
          maxRadiusPx,
          smoothingIter,
        );
        if (!smoothed) {
          logger.debug(`[RegionGrowTool] Propagation stopped at slice ${idx}: fill failed`);
          break;
        }

        // Convert pixel→world using target slice's imagePlaneModule (no viewport navigation)
        const targetIPM = cornerstone.metaData.get("imagePlaneModule", targetImageId);
        if (!targetIPM?.imagePositionPatient) break;

        const worldPoints = pixelsToWorld(smoothed, targetIPM, targetImage);
        if (!worldPoints || worldPoints.length === 0) break;

        // Build annotation for the target slice
        const uid =
          csUtils.uuidv4?.() ?? `rg-prop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const annotation = {
          annotationUID: uid,
          metadata: {
            toolName: "PlanarFreehandContourSegmentationTool",
            referencedImageId: targetImageId,
            FrameOfReferenceUID: forUID,
            viewPlaneNormal: [...viewPlaneNormal],
            viewUp: [...viewUp],
            sliceIndex: idx,
          },
          data: {
            contour: { polyline: worldPoints, closed: true },
            segmentation: { segmentationId: segId, segmentIndex: _segmentIndex },
            handles: {
              points: [],
              activeHandleIndex: null,
              textBox: {
                hasMoved: false,
                worldPosition: [0, 0, 0],
                worldBoundingBox: {
                  topLeft: [0, 0, 0],
                  topRight: [0, 0, 0],
                  bottomLeft: [0, 0, 0],
                  bottomRight: [0, 0, 0],
                },
              },
            },
          },
          highlighted: false,
          isLocked: false,
          isVisible: true,
          invalidated: false,
          _regionGrow: true,
        };

        // Mark as auto-generated so InterpolationManager skips these slices
        // and doesn't create duplicate contours that crash the merge algorithm.
        (annotation as any).autoGenerated = true;
        (annotation as any)._regionGrow = true;

        cornerstoneTools.annotation.state.addAnnotation(annotation, element);
        try {
          cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(
            annotation,
          );
        } catch (err) {
          console.error(
            `[RegionGrowTool] addContourSegmentationAnnotation failed on slice ${idx}:`,
            err,
          );
        }
        // Do NOT call triggerAnnotationCompleted for propagated contours —
        // it triggers InterpolationManager which creates duplicate contours
        // on slices that already have region-grow annotations, causing
        // Cornerstone's merge/subtraction to fail with infinite loops.

        localUIDs.push(uid);
        _regionGrowImageIds.add(targetImageId);
        annotatedImageIds.add(targetImageId);

        logger.debug(`[RegionGrowTool] ✓ Propagated to slice ${idx} (${worldPoints.length} pts)`);

        prevContour = smoothed;

        // Yield to the browser every few slices so the UI stays responsive
        // and previously rendered contours can paint.
        if (localUIDs.length % 3 === 0) {
          try {
            cornerstoneTools.utilities.triggerAnnotationRender(element);
          } catch (e) {
            logger.debug("[RegionGrowTool] triggerAnnotationRender skipped:", e);
          }
          await new Promise((r) => setTimeout(r, 0));
        }
      }
    }

    // Propagate both directions (no viewport navigation, so can run concurrently)
    await Promise.all([
      propagateDirection(+1, initialContour),
      propagateDirection(-1, initialContour),
    ]);

    // Re-render annotations and notify stats overlay
    try {
      cornerstoneTools.utilities.triggerAnnotationRender(element);
      // Fire ANNOTATION_MODIFIED so SegmentationStatsOverlay recomputes.
      // Region grow bypasses triggerAnnotationCompleted (to avoid InterpolationManager),
      // so the stats overlay wouldn't otherwise know contours were added.
      cornerstone.eventTarget?.dispatchEvent(
        new CustomEvent("CORNERSTONE_TOOLS_ANNOTATION_MODIFIED", { detail: {} }),
      );
    } catch (e) {
      logger.debug("[RegionGrowTool] post-propagation render skipped:", e);
    }
    // Refresh the 3D viewer with the freshly propagated slices. Without this,
    // the SegmentationViewerPanel's `>= 2 axial slices` gate keeps the 3D
    // viewer hidden in fallback mode (the initial refresh fires before this
    // async propagation completes, so it sees only the seed slice).
    if (_hasNnInteractiveSession()) {
      refreshViewerFromAnnotations();
    }
  }

  /**
   * After a primary-plane fallback contour is committed, project the same
   * world-space click into each companion viewport and run local flood-fill
   * there too — so one click yields up to 3 contours (parity with the GPU
   * companion path). Auto-propagates adjacent slices on each companion.
   *
   * Reuses the primary's HU range (`primaryEffectiveMin`/`Max`) and seedHU
   * directly. The same physical stone has the same HU in every view, so
   * recomputing the range from a possibly-drifted companion seed (which
   * tends to land on contrast-enhanced parenchyma) is what made companion
   * fills swallow the whole kidney on contrast studies.
   *
   * Also passes the primary's contour pixel area as a sanity check — if a
   * companion's fill grows much larger than the primary, the fill almost
   * certainly leaked across a partial-volume bridge into parenchyma and the
   * companion is rejected rather than producing a kidney-sized contour.
   *
   * Tracks every UID it creates in `_fallbackPerPlane[plane]` so per-plane
   * Ctrl+Z (Reject for the active viewport) can clear a single companion
   * without touching the primary or the other companion.
   */
  async function runFallbackOnCompanions(
    worldPoint: Pt3,
    primaryPlane: PlaneKey,
    primarySeedHU: number,
    primaryEffectiveMin: number,
    primaryEffectiveMax: number,
    primaryContourAreaPx: number,
    token: number,
  ): Promise<void> {
    // ENG-280: the segmentation study keeps one stone on the clicked series only.
    if (getDefaultStore().get(companionMasksBlockedAtom)) return;
    const sibs = getSiblingViewports();
    if (!sibs) return;
    const primarySib = sibs[primaryPlane];
    const primarySeriesUid = primarySib?.seriesUid ?? null;

    // Track each companion's seed slice index so we can fire one sync event
    // at the end with the projected positions for all 3 scrollbars.
    // Each companion is independent; run them in parallel so the user only
    // pays the slowest plane's latency, not the sum.
    const companionResults = await Promise.all(
      PLANE_KEYS.filter((p) => p !== primaryPlane).map(async (plane) => {
        const sib = sibs[plane];
        const compViewport = sib?.viewport;
        const compSeriesUid = sib?.seriesUid;
        if (!compViewport || !compSeriesUid) {
          console.warn(
            `[RegionGrowTool] Fallback companion (${plane}) skipped: no viewport or seriesUid`,
          );
          return [plane, null] as const;
        }
        // Don't re-segment the same series in two viewports.
        if (compSeriesUid === primarySeriesUid) {
          console.warn(
            `[RegionGrowTool] Fallback companion (${plane}) skipped: same series as primary`,
          );
          return [plane, null] as const;
        }
        // Skip companions that already have contours — only propagate to empty
        // planes (parity with nnInteractive's gate at nnInteractiveSession.ts).
        // Covers both prior nn sessions and prior fallback contours.
        const priorSession = getMultiSessions().get(plane);
        const priorFallback = _fallbackPerPlane.get(plane);
        const priorFallbackHasContours = !!(
          priorFallback &&
          (priorFallback.seedUID || priorFallback.propagatedUIDs.length > 0)
        );
        if ((priorSession && priorSession.contourUIDs.length > 0) || priorFallbackHasContours) {
          logger.debug(
            `[RegionGrowTool] Fallback companion (${plane}) skipped: already has contours`,
          );
          return [plane, null] as const;
        }
        logger.debug(`[RegionGrowTool] Fallback companion (${plane}) starting …`);
        try {
          const seedZ = await runFallbackForCompanion(
            worldPoint,
            plane,
            compViewport,
            compSeriesUid,
            primarySeedHU,
            primaryEffectiveMin,
            primaryEffectiveMax,
            primaryContourAreaPx,
            token,
          );
          return [plane, seedZ] as const;
        } catch (err) {
          console.warn(`[RegionGrowTool] Fallback companion (${plane}) failed:`, err);
          return [plane, null] as const;
        }
      }),
    );
    // User may have rejected during the await above — drop without firing the
    // cross-viewport sync (which would otherwise scroll empty viewports).
    if (token !== _fallbackCompanionToken) {
      logger.debug(`[RegionGrowTool] Fallback companion propagation cancelled (token stale)`);
      return;
    }
    const seedSliceByPlane = new Map<PlaneKey, number>();
    for (const [plane, seedZ] of companionResults) {
      if (seedZ != null) seedSliceByPlane.set(plane, seedZ);
    }

    // Fire the cross-viewport sync so each scrollbar paints its orange
    // "projected slice" tick + highlighted thumb on the seed slice.
    // Mirrors what the GPU companion path does on success.
    // getCurrentImageIdIndex returns the K-step; on reversed-HTJ2K canonicalized
    // volumes that differs from list-index by N-1-i, and HangingProtocolGrid's
    // nnCrossViewportSync handler treats the normalized value as a LIST-INDEX —
    // feeding raw K-step snaps the (primary) pane to the MIRROR slice (the real
    // root of baseline "centroid-jump #5"). Resolve via getCurrentImageId →
    // imageIds.indexOf to stay in list-index frame, falling back to raw K-step +
    // warn. Same fix already applied in nnInteractiveSession.getCurrentNormalized.
    const currentListIndexNorm = (vp: any, ids: string[]): number => {
      const currentImageId: string | undefined = vp?.getCurrentImageId?.();
      const listIdx = currentImageId ? ids.indexOf(currentImageId) : -1;
      if (listIdx < 0) {
        logger.warn(
          "[RegionGrowTool] normalizedFor: currentImageId not in imageIds; falling back to K-step",
        );
        const idx = vp?.getCurrentImageIdIndex?.() ?? 0;
        return idx / (ids.length - 1);
      }
      return listIdx / (ids.length - 1);
    };
    const normalizedFor = (plane: PlaneKey): number => {
      const vp = sibs[plane]?.viewport;
      const ids: string[] = vp?.getImageIds?.() ?? [];
      if (ids.length <= 1) return 0.5;
      // Primary plane: keep the user's currently-displayed slice.
      // Companion planes that got a fresh seed: snap to the seed slice.
      // Companion planes that were skipped (already had contours): keep
      // their current slice — clicking on one plane shouldn't jolt views
      // that already have annotations.
      if (plane === primaryPlane) {
        return currentListIndexNorm(vp, ids);
      }
      const seedZ = seedSliceByPlane.get(plane);
      if (seedZ == null) {
        return currentListIndexNorm(vp, ids);
      }
      return seedZ / (ids.length - 1);
    };
    notifyNnCrossViewportSync({
      axial: normalizedFor("axial"),
      coronal: normalizedFor("coronal"),
      sagittal: normalizedFor("sagittal"),
    });
  }

  /** Polygon area via the shoelace formula. Sign-agnostic; returns absolute value. */
  function polygonAreaPx(points: Pt2[]): number {
    if (points.length < 3) return 0;
    let s = 0;
    for (let i = 0; i < points.length; i++) {
      const [r1, c1] = points[i];
      const [r2, c2] = points[(i + 1) % points.length];
      s += c1 * r2 - c2 * r1;
    }
    return Math.abs(s) * 0.5;
  }

  /** Run the fallback flow for a single companion viewport.
   *  Returns the seed slice index on success (whether the contour came from
   *  the fill pipeline or the synthesized placeholder), null on rejection. */
  async function runFallbackForCompanion(
    worldPoint: Pt3,
    plane: PlaneKey,
    compViewport: any,
    compSeriesUid: string,
    primarySeedHU: number,
    effectiveMin: number,
    effectiveMax: number,
    primaryContourAreaPx: number,
    token: number,
  ): Promise<number | null> {
    const isCancelled = (): boolean => token !== _fallbackCompanionToken;
    const compElement = compViewport.element;
    const compImgData = compViewport.getImageData?.();

    // Project click → companion's NATIVE voxel space [z, y, x] using DICOM
    // imagePositionPatient + row/col cosines + slice direction. vtkImageData's
    // worldToIndex doesn't give native voxels for a companion viewport whose
    // volume was reformatted from a different acquisition orientation, so we
    // use the same helper the GPU companion path uses.
    const voxel = worldToNativeVoxelZYX(worldPoint, compViewport, cornerstone);
    if (!voxel) {
      console.warn(`[RegionGrowTool] Companion (${plane}) projection failed`);
      return null;
    }
    const zVox = Math.round(voxel[0]);
    const yVox = Math.round(voxel[1]);
    const xVox = Math.round(voxel[2]);

    const compImageIds: string[] = compViewport.getImageIds?.() ?? [];
    if (zVox < 0 || zVox >= compImageIds.length) {
      console.warn(
        `[RegionGrowTool] Companion (${plane}) projected slice ${zVox} out of range [0, ${compImageIds.length})`,
      );
      return null;
    }
    logger.info(
      `[RegionGrowTool] Companion (${plane}) voxel=[${zVox},${yVox},${xVox}] sliceCount=${compImageIds.length}`,
    );

    // Snap seed to brightest neighbor — projection error can land it on soft tissue.
    const snapped = await snapCompanionSeed(compViewport, [zVox, yVox, xVox], worldPoint);
    if (isCancelled()) return null;
    const [seedZ, seedRow, seedCol] = snapped;
    if (seedZ < 0 || seedZ >= compImageIds.length) return null;

    // Load the companion's seed slice's pixel data directly so we don't have
    // to navigate (and the fill works regardless of the user's current scroll
    // position on this viewport).
    const seedImageId = compImageIds[seedZ];
    let seedImage: any;
    try {
      seedImage = await cornerstone.imageLoader.loadAndCacheImage(seedImageId);
    } catch (err) {
      logger.debug(`[RegionGrowTool] Companion (${plane}) image load failed:`, err);
      return null;
    }
    if (isCancelled()) return null;
    let rawPixelData = seedImage?.getPixelData?.();
    const compCols = seedImage?.columns;
    const compRows = seedImage?.rows;
    if (!rawPixelData || !compCols || !compRows) return null;

    // Same Uint16-masking-signed-int16 issue as readHUAtCanvas — reinterpret
    // so negative HU values (air, fat, lung) read as negatives instead of
    // wrapping to 65000+ and poisoning adaptiveMinHU's bgMean.
    if (rawPixelData instanceof Uint16Array) {
      rawPixelData = new Int16Array(
        rawPixelData.buffer,
        rawPixelData.byteOffset,
        rawPixelData.length,
      );
    }

    const slope = seedImage.slope ?? 1;
    const intercept = seedImage.intercept ?? 0;
    const isPreScaled = !!seedImage.preScale;
    let huData: any;
    if (isPreScaled || (slope === 1 && intercept === 0)) {
      huData = rawPixelData;
    } else {
      huData = new Float32Array(rawPixelData.length);
      for (let i = 0; i < rawPixelData.length; i++) {
        huData[i] = rawPixelData[i] * slope + intercept;
      }
    }

    // Validate the snapped seed actually landed on something stone-like.
    // Partial-volume averaging on thicker companion slices can shave a
    // small stone's measured HU well below the primary's, so just require
    // the seed sit inside the primary's effective HU range. The harder
    // protection against parenchyma over-segmentation is the post-fill
    // area cap below — it catches the case where the seed is plausible
    // but the fill bridges through partial-volume into kidney tissue.
    const cfg = _config;
    if (!cfg) return null;
    const seedHU = (huData[seedRow * compCols + seedCol] as number) ?? 0;
    if (seedHU < effectiveMin) {
      console.warn(
        `[RegionGrowTool] Companion (${plane}) seedHU=${seedHU.toFixed(0)} below effectiveMin ` +
          `${effectiveMin.toFixed(0)} (primary=${primarySeedHU.toFixed(0)}) — skip`,
      );
      return null;
    }
    if (seedHU > effectiveMax) {
      console.warn(
        `[RegionGrowTool] Companion (${plane}) seedHU=${seedHU.toFixed(0)} above effectiveMax ${effectiveMax.toFixed(0)} — skip`,
      );
      return null;
    }

    const compSpacing = compImgData?.spacing;
    const compPixelSpacing = compSpacing ? (compSpacing[0] + compSpacing[1]) / 2 : 1;
    const maxRadiusPx = (cfg.max_radius_mm ?? 50) / compPixelSpacing;
    const smoothingIter = cfg.smoothing_iterations ?? 2;

    const seedSmoothed = runFillPipelineOnSlice(
      huData,
      compCols,
      compRows,
      seedRow,
      seedCol,
      effectiveMin,
      effectiveMax,
      maxRadiusPx,
      smoothingIter,
    );
    if (!seedSmoothed) {
      console.warn(
        `[RegionGrowTool] Companion (${plane}) seed fill produced no contour ` +
          `(seedHU=${seedHU.toFixed(0)} effMin=${effectiveMin.toFixed(0)} effMax=${effectiveMax.toFixed(0)})`,
      );
      return null;
    }

    // Size sanity check — same physical stone should produce a contour of
    // similar pixel area in each view. If the companion's fill is much
    // bigger than the primary's, it leaked across a partial-volume bridge
    // into parenchyma. Reject rather than render a kidney-sized contour.
    // The absolute floor (500 px²) handles the case where the primary
    // contour is tiny and we'd otherwise be too restrictive.
    const seedAreaPx = polygonAreaPx(seedSmoothed);
    const maxAcceptableAreaPx = Math.max(primaryContourAreaPx * 5, 500);
    if (seedAreaPx > maxAcceptableAreaPx) {
      console.warn(
        `[RegionGrowTool] Companion (${plane}) seed area=${seedAreaPx.toFixed(0)} px² ` +
          `exceeds cap ${maxAcceptableAreaPx.toFixed(0)} px² (primary=${primaryContourAreaPx.toFixed(0)} px²) — skip`,
      );
      return null;
    }
    logger.debug(
      `[RegionGrowTool] Companion (${plane}) seed accepted: slice=${seedZ} ` +
        `seedHU=${seedHU.toFixed(0)} area=${seedAreaPx.toFixed(0)} px² (primary=${primaryContourAreaPx.toFixed(0)} px²)`,
    );

    // Final cancellation check before we commit any annotation state — avoids
    // orphan contours appearing after the user has already pressed Esc.
    if (isCancelled()) return null;

    // Build seed contour annotation referenced to the companion's seed slice.
    // isSeed=true → user-visible keyframe (gets scrollbar marker, not autoGenerated).
    const seedUID = commitCompanionContour(
      compViewport,
      compElement,
      seedImageId,
      seedZ,
      seedSmoothed,
      false, // autoGenerated=false — this is the user's seed click on this companion
    );
    if (!seedUID) return null;
    _fallbackPerPlane.set(plane, {
      seedUID,
      propagatedUIDs: [],
      element: compElement,
    });
    _regionGrowImageIds.add(seedImageId);

    // Navigate the companion viewport to the seed slice so the user sees the
    // contour without having to scroll. Match the spec: the click should
    // produce visible output on all three views.
    try {
      compViewport.setImageIdIndex?.(seedZ);
    } catch (err) {
      logger.debug(`[RegionGrowTool] Companion (${plane}) setImageIdIndex skipped:`, err);
    }

    try {
      cornerstoneTools.utilities.triggerAnnotationRender(compElement);
    } catch (err) {
      logger.debug(`[RegionGrowTool] Companion (${plane}) render skipped:`, err);
    }

    // Auto-propagate the companion's seed contour to adjacent slices.
    if (cfg.auto_propagate) {
      await propagateCompanionFromSeed(
        plane,
        compViewport,
        compElement,
        compImageIds,
        seedZ,
        seedSmoothed,
        effectiveMin,
        effectiveMax,
        maxRadiusPx,
        smoothingIter,
        maxAcceptableAreaPx,
      );
    }

    // Ensure a fallback session exists so the 3D viewer + future UI work,
    // and so isFallbackOnly() reports true across all touched planes.
    try {
      ensureSessionForFallback(compViewport, plane, compSeriesUid);
    } catch (err) {
      logger.debug(`[RegionGrowTool] Companion (${plane}) ensureSession skipped:`, err);
    }
    refreshViewerFromAnnotations();
    return seedZ;
  }

  /** Build + register a propagated/seed contour annotation on a companion's slice.
   *  Returns the annotation UID, or null on failure. The `autoGenerated` flag
   *  controls scrollbar-marker visibility: false for the seed slice (user
   *  keyframe → marker shown), true for propagated slices (no marker, parity
   *  with primary's `propagateToAdjacentSlices`). */
  function commitCompanionContour(
    compViewport: any,
    compElement: any,
    targetImageId: string,
    sliceIndex: number,
    smoothed: Pt2[],
    autoGenerated: boolean,
  ): string | null {
    const targetIPM = cornerstone.metaData.get("imagePlaneModule", targetImageId);
    if (!targetIPM?.imagePositionPatient) return null;

    const ipp = targetIPM.imagePositionPatient;
    const rowCos = targetIPM.rowCosines ?? [1, 0, 0];
    const colCos = targetIPM.columnCosines ?? [0, 1, 0];
    const cSp = targetIPM.columnPixelSpacing ?? 1;
    const rSp = targetIPM.rowPixelSpacing ?? 1;
    const worldPoints: Pt3[] = smoothed.map(
      ([r, c]) =>
        [
          ipp[0] + c * cSp * rowCos[0] + r * rSp * colCos[0],
          ipp[1] + c * cSp * rowCos[1] + r * rSp * colCos[1],
          ipp[2] + c * cSp * rowCos[2] + r * rSp * colCos[2],
        ] as Pt3,
    );
    if (worldPoints.length === 0) return null;

    const activeSeg = cornerstoneTools.segmentation.getActiveSegmentation?.(compViewport.id);
    const segId =
      activeSeg?.segmentationId ??
      cornerstoneTools.segmentation.state.getSegmentations()?.[0]?.segmentationId ??
      "unknown";
    const camera = compViewport.getCamera?.() ?? {};
    const viewPlaneNormal = camera.viewPlaneNormal ?? [0, 0, 1];
    const viewUp = camera.viewUp ?? [0, -1, 0];
    const forUID = compViewport.getFrameOfReferenceUID?.();
    const uid =
      csUtils.uuidv4?.() ?? `rg-comp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const annotation: any = {
      annotationUID: uid,
      metadata: {
        toolName: "PlanarFreehandContourSegmentationTool",
        referencedImageId: targetImageId,
        FrameOfReferenceUID: forUID,
        viewPlaneNormal: [...viewPlaneNormal],
        viewUp: [...viewUp],
        sliceIndex,
      },
      data: {
        contour: { polyline: worldPoints, closed: true },
        segmentation: { segmentationId: segId, segmentIndex: _segmentIndex },
        handles: {
          points: [],
          activeHandleIndex: null,
          textBox: {
            hasMoved: false,
            worldPosition: [0, 0, 0],
            worldBoundingBox: {
              topLeft: [0, 0, 0],
              topRight: [0, 0, 0],
              bottomLeft: [0, 0, 0],
              bottomRight: [0, 0, 0],
            },
          },
        },
      },
      highlighted: false,
      isLocked: false,
      isVisible: true,
      invalidated: false,
      autoGenerated,
      _regionGrow: true,
    };
    cornerstoneTools.annotation.state.addAnnotation(annotation, compElement);
    try {
      cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(annotation);
    } catch (err) {
      console.warn("[RegionGrowTool] Companion addContourSegmentationAnnotation failed:", err);
    }
    // Fire ANNOTATION_COMPLETED for the seed slice only — same as the primary
    // path. This wires up the scrollbar marker / Save button state. Skip for
    // propagated slices to avoid InterpolationManager creating duplicates.
    if (!autoGenerated) {
      try {
        cornerstoneTools.annotation.state.triggerAnnotationCompleted(annotation);
      } catch (err) {
        logger.debug("[RegionGrowTool] Companion triggerAnnotationCompleted skipped:", err);
      }
    }
    return uid;
  }

  /** Propagate a companion's seed contour to adjacent slices in both directions.
   *  Enforces the same per-slice area cap as the seed slice — if a propagated
   *  slice's fill exceeds it, propagation in that direction stops rather than
   *  letting partial-volume bridges run the contour into parenchyma. */
  async function propagateCompanionFromSeed(
    plane: PlaneKey,
    compViewport: any,
    compElement: any,
    allImageIds: string[],
    seedSliceIndex: number,
    seedContour: Pt2[],
    effectiveMin: number,
    effectiveMax: number,
    maxRadiusPx: number,
    smoothingIter: number,
    maxAcceptableAreaPx: number,
  ): Promise<void> {
    // Skip slices that already have annotations for this segment.
    const allAnns: any[] = cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
    const annotatedImageIds = new Set<string>();
    for (const ann of allAnns) {
      if (
        ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
        ann.data?.segmentation?.segmentIndex === _segmentIndex &&
        ann.metadata?.referencedImageId
      ) {
        annotatedImageIds.add(ann.metadata.referencedImageId);
      }
    }

    const propagateDir = async (delta: number, startContour: Pt2[]): Promise<void> => {
      let prevContour = startContour;
      for (let idx = seedSliceIndex + delta; idx >= 0 && idx < allImageIds.length; idx += delta) {
        const targetImageId = allImageIds[idx];
        if (annotatedImageIds.has(targetImageId)) break;

        let targetImage: any;
        try {
          targetImage = await cornerstone.imageLoader.loadAndCacheImage(targetImageId);
        } catch (err) {
          logger.debug(`[RegionGrowTool] Companion (${plane}) loadAndCacheImage failed:`, err);
          break;
        }
        const rawPixelData = targetImage.getPixelData?.();
        const targetCols = targetImage.columns;
        const targetRows = targetImage.rows;
        if (!rawPixelData || !targetCols || !targetRows) break;

        const slope = targetImage.slope ?? 1;
        const intercept = targetImage.intercept ?? 0;
        const isPreScaled = !!targetImage.preScale;
        let huData: any;
        if (isPreScaled || (slope === 1 && intercept === 0)) {
          huData = rawPixelData;
        } else {
          huData = new Float32Array(rawPixelData.length);
          for (let i = 0; i < rawPixelData.length; i++) {
            huData[i] = rawPixelData[i] * slope + intercept;
          }
        }

        const [seedRow, seedCol] = contourCentroid(prevContour);
        if (seedRow < 0 || seedRow >= targetRows || seedCol < 0 || seedCol >= targetCols) break;
        const snapped = snapToBrightest(huData, targetCols, targetRows, seedRow, seedCol, 10);
        if (snapped.hu < effectiveMin) break;

        const smoothed = runFillPipelineOnSlice(
          huData,
          targetCols,
          targetRows,
          snapped.row,
          snapped.col,
          effectiveMin,
          effectiveMax,
          maxRadiusPx,
          smoothingIter,
        );
        if (!smoothed) break;
        // Stop propagation as soon as a slice fills past the cap — same
        // partial-volume failure mode as the seed slice.
        if (polygonAreaPx(smoothed) > maxAcceptableAreaPx) {
          logger.debug(
            `[RegionGrowTool] Companion (${plane}) propagation at slice ${idx} exceeded area cap — stop`,
          );
          break;
        }

        // Propagated slices: autoGenerated=true → no scrollbar marker, parity
        // with primary's `propagateToAdjacentSlices`.
        const uid = commitCompanionContour(
          compViewport,
          compElement,
          targetImageId,
          idx,
          smoothed,
          true,
        );
        if (!uid) break;
        const planeEntry = _fallbackPerPlane.get(plane);
        if (planeEntry) planeEntry.propagatedUIDs.push(uid);
        _regionGrowImageIds.add(targetImageId);
        annotatedImageIds.add(targetImageId);
        prevContour = smoothed;

        if ((planeEntry?.propagatedUIDs.length ?? 0) % 3 === 0) {
          try {
            cornerstoneTools.utilities.triggerAnnotationRender(compElement);
          } catch (err) {
            logger.debug(`[RegionGrowTool] Companion (${plane}) render skipped:`, err);
          }
          await new Promise((r) => setTimeout(r, 0));
        }
      }
    };

    await Promise.all([propagateDir(+1, seedContour), propagateDir(-1, seedContour)]);
    try {
      cornerstoneTools.utilities.triggerAnnotationRender(compElement);
    } catch (err) {
      logger.debug(`[RegionGrowTool] Companion (${plane}) final render skipped:`, err);
    }
    // Sync the 3D viewer once propagation has settled — same rationale as
    // the primary's propagateToAdjacentSlices.
    refreshViewerFromAnnotations();
  }

  class RegionGrowTool extends BaseTool {
    static toolName = "RegionGrowTool";

    constructor(toolProps: any = {}, defaultToolProps: any = {}) {
      super(toolProps, {
        name: "RegionGrowTool",
        bindings: [],
        ...defaultToolProps,
      });
    }

    preMouseDownCallback(evt: any): boolean {
      const { element, currentPoints } = evt.detail;
      const nativeEvt = evt.detail.event;
      const isShift = nativeEvt?.shiftKey ?? false;
      // Use || not ?? — ctrlKey=false is not nullish, so ?? wouldn't check metaKey.
      // On macOS Ctrl+Click = right-click, so Cmd+Click (metaKey) is needed.
      const isCtrl = !!(nativeEvt?.ctrlKey || nativeEvt?.metaKey);

      const enabledElement = getEnabledElement(element);
      if (!enabledElement?.viewport) {
        logger.warn("[RegionGrowTool] click ignored — no enabled viewport");
        return false;
      }
      const viewport = enabledElement.viewport as any;
      const canvasPoint: Pt2 = currentPoints.canvas;

      const result = readHUAtCanvas(viewport, canvasPoint, csUtils);
      if (!result) {
        logger.warn(
          "[RegionGrowTool] click ignored — readHUAtCanvas returned null (no scalar data at this slice / out-of-bounds / volume not yet streamed)",
          { canvasPoint, viewportId: viewport?.id },
        );
        return false;
      }

      const { row: rawRow, col: rawCol, dimensions, scalarData, imageData } = result;
      const [cols, rows] = dimensions;

      if (!_config || !_config.enabled) {
        logger.warn(
          "[RegionGrowTool] click ignored — segmentation-assist config not loaded or disabled",
          { hasConfig: !!_config, enabled: _config?.enabled },
        );
        return false;
      }

      // Snap to the brightest pixel in a small neighborhood so the threshold
      // is based on the stone's peak intensity, not the exact click position.
      // This ensures two clicks on the same stone produce the same contour.
      const snapped = snapToBrightest(scalarData, cols, rows, rawRow, rawCol, 3);
      const seedHU = snapped.hu;
      const row = snapped.row;
      const col = snapped.col;

      // Block clicks while nnInteractive request is in flight
      if (getNnRequestInFlight()) {
        logger.info("[RegionGrowTool] click ignored — nnInteractive request in flight");
        return true;
      }

      // Resolve sibling viewports and determine which plane was clicked.
      // Moved before stone floor check so contrast detection is available (#167).
      const siblings = getSiblingViewports();
      const clickedPlane: PlaneKey = (() => {
        if (!siblings) return "axial";
        if (viewport.id === siblings.coronal?.viewport?.id) return "coronal";
        if (viewport.id === siblings.sagittal?.viewport?.id) return "sagittal";
        return "axial";
      })();

      // Item 5: click-2 confirm when seeding on a slice that already has an
      // in-progress contour for the current segment. The first click on such
      // a slice shows a toast warning and returns without seeding; a second
      // click on the same slice within OVERWRITE_CONFIRM_WINDOW_MS proceeds
      // and overwrites. Clicking any unannotated slice resets the pending
      // state so re-warning is required if the user returns to the annotated
      // slice. Runs BEFORE the stoneFloor reject so the warning fires
      // regardless of whether the user clicks on a stone pixel or soft tissue.
      // Skipped for Shift/Ctrl/Cmd refinements (those are intentional
      // re-clicks on existing seeds). The in-flight preview UID is excluded
      // so the existing same-slice preview-replacement path keeps working.
      if (!isShift && !isCtrl) {
        const currentImageIdForCheck = viewport.getCurrentImageId?.();
        if (currentImageIdForCheck) {
          const allAnns: any[] = cornerstoneTools.annotation.state.getAllAnnotations() ?? [];
          const hasCommittedContour = allAnns.some(
            (ann) =>
              ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
              ann.data?.segmentation?.segmentIndex === _segmentIndex &&
              ann.metadata?.referencedImageId === currentImageIdForCheck &&
              !isPreviewUID(ann.annotationUID),
          );
          if (hasCommittedContour) {
            const now = Date.now();
            const pending = _pendingOverwriteSlice;
            if (
              pending &&
              pending.imageId === currentImageIdForCheck &&
              now - pending.at < OVERWRITE_CONFIRM_WINDOW_MS
            ) {
              _pendingOverwriteSlice = null;
              // fall through and proceed with the click
            } else {
              _pendingOverwriteSlice = { imageId: currentImageIdForCheck, at: now };
              showNnToast("Slice already annotated — click again to overwrite");
              return true;
            }
          } else {
            _pendingOverwriteSlice = null;
          }
        }
      }

      // Reject clicks on soft tissue — the snapped seed must be above the
      // stone detection floor. Without this, a soft tissue click can produce
      // a tiny nonsensical contour from borderline pixels.
      // On contrast-enhanced series, lower the floor since contrast shifts HU (#167).
      const isContrast = siblings?.[clickedPlane]?.hasContrast ?? false;
      // The bright HU floor is a STONE-only guard. Soft-tissue targets (organ
      // parenchyma ~15–60 HU, lymph nodes, most lesions) sit far below it, so we
      // apply the floor only in stone mode — otherwise a valid soft-tissue click
      // would be discarded as "not bright enough" instead of reaching nnInteractive.
      const isStoneMode = getNnContext()?.isStone === true;
      const stoneFloor = isContrast
        ? Math.min(_config?.fwhm_floor ?? DEFAULT_FWHM_FLOOR, CONTRAST_FWHM_FLOOR)
        : (_config?.fwhm_floor ?? DEFAULT_FWHM_FLOOR);
      if (isStoneMode && seedHU < stoneFloor) {
        logger.warn(
          `[RegionGrowTool] click ignored — seed HU=${seedHU.toFixed(0)} below stone floor (${stoneFloor}); click on a brighter pixel`,
          { seedHU, stoneFloor, isContrast, clickedPlane },
        );
        return false;
      }
      logger.info(
        `[RegionGrowTool] click accepted — seedHU=${seedHU.toFixed(0)} plane=${clickedPlane}`,
        {
          mode: isShift ? "shift" : isCtrl ? "ctrl" : "plain",
          nnSessions: getMultiSessions().size,
          hasContext: !!_cachedContext,
        },
      );

      // Get world-space click point for multi-series dispatch
      const worldPoint: Pt3 = viewport.canvasToWorld(canvasPoint);

      // ── nnInteractive path: Shift+Click (positive refinement) ─────────
      if (isShift && getMultiSessions().size > 0 && _cachedContext) {
        // In fallback mode the GPU is unreachable, so refinement clicks
        // would just retry the same failed call. Disable them entirely.
        if (isFallbackOnly()) return false;
        handleMultiSeriesNnClick(
          worldPoint,
          clickedPlane,
          "positive",
          { ..._cachedContext, viewport, element }, // Use clicked viewport for spinner
          _segmentIndex,
          _regionGrowImageIds,
          makeCommitDispatcher(viewport?.id ?? null),
          makeAutoSwitchDispatcher(viewport?.id ?? null),
          true,
        );
        return true;
      }

      // ── nnInteractive path: Ctrl+Click (negative refinement) ──────────
      if (isCtrl && getMultiSessions().size > 0 && _cachedContext) {
        if (isFallbackOnly()) return false;
        handleMultiSeriesNnClick(
          worldPoint,
          clickedPlane,
          "negative",
          { ..._cachedContext, viewport, element }, // Use clicked viewport for spinner
          _segmentIndex,
          _regionGrowImageIds,
          makeCommitDispatcher(viewport?.id ?? null),
          makeAutoSwitchDispatcher(viewport?.id ?? null),
          true,
        );
        return true;
      }

      // ── Legacy local path: Shift+Click (add seed) ────────────────────
      if (isShift && _previewUID && _cachedContext && getMultiSessions().size === 0) {
        const transaction = beginThresholdUndo(viewport);
        _seeds.push([row, col]);
        applyThresholdPreview(transaction, true);
        return true;
      }

      // ── Legacy local path: Ctrl+Click (add exclusion) ────────────────
      if (isCtrl && _previewUID && _cachedContext && getMultiSessions().size === 0) {
        const transaction = beginThresholdUndo(viewport);
        _exclusions.push([rawRow, rawCol]);
        applyThresholdPreview(transaction, true);
        return true;
      }

      // Plain click: start a new contour. Previous clicks are already committed.
      // If clicking the same slice, remove the previous annotation on that slice
      // so the new one replaces it.
      const currentImageId = viewport.getCurrentImageId?.();
      const thresholdUndo = beginThresholdUndo(viewport);
      if (!thresholdUndo && _previewUID && _previewImageId === currentImageId) {
        removePreview(); // same slice — replace
      }
      _seeds.length = 0;
      _exclusions.length = 0;

      const {
        hu_range,
        hu_tolerance,
        max_radius_mm,
        smoothing_iterations,
        threshold_mode,
        fwhm_floor,
      } = _config;
      let effectiveMin: number;
      let effectiveMax: number;

      if (threshold_mode === "fwhm") {
        // True FWHM (Demehri 2011): threshold = (background + seed) / 2.
        // Sampling local background means we exclude contrast-enhanced
        // renal cortex automatically — a 500 HU stone on contrast with
        // ~200 HU cortex gets a 350 HU threshold, which keeps the fill
        // inside the stone instead of leaking into the kidney.
        //
        // Previously this branch used `seedHU / 3` which had no anchor
        // to the surrounding tissue and routinely engulfed the kidney on
        // small-stone cases.
        const floor = isContrast
          ? Math.min(fwhm_floor ?? DEFAULT_FWHM_FLOOR, CONTRAST_FWHM_FLOOR)
          : (fwhm_floor ?? DEFAULT_FWHM_FLOOR);
        const bgMean = sampleBackgroundMeanHU(scalarData, cols, rows, row, col);
        // Log whether the background ring sample succeeded so we can tell
        // how often the fallback (seedHU * 0.5) is hit — happens when the
        // seed is in a corner small enough that no ring pixel is in-bounds.
        // Per the heuristic-helpers rule: helpers that silently return null
        // on miss must surface a counter at the call site.
        logger.info("region_grow_bg_sample_coverage", {
          seedRow: row,
          seedCol: col,
          hit: bgMean !== null,
          bgMean,
          seedHU,
        });
        const fwhmThreshold = bgMean !== null ? bgMean + (seedHU - bgMean) * 0.5 : seedHU * 0.5;
        effectiveMin = Math.max(fwhmThreshold, floor);
        effectiveMax = hu_range
          ? hu_range[1]
          : seedHU + (hu_tolerance ?? (isContrast ? CONTRAST_HU_TOLERANCE : DEFAULT_HU_TOLERANCE));
        if (effectiveMin > effectiveMax) {
          console.warn(
            `[RegionGrow] FWHM mode: inverted HU range [${effectiveMin}, ${effectiveMax}] for seedHU=${seedHU}. Clamping.`,
          );
          effectiveMin = Math.min(effectiveMin, effectiveMax - 1);
        }
      } else {
        effectiveMin = hu_range ? hu_range[0] : seedHU - (hu_tolerance ?? 20);
        effectiveMax = hu_range ? hu_range[1] : seedHU + (hu_tolerance ?? 20);
      }
      const spacing = imageData?.spacing;
      const pixelSpacing = spacing ? (spacing[0] + spacing[1]) / 2 : 1;
      const maxRadiusPx = (max_radius_mm ?? 50) / pixelSpacing;

      // Cache context for multi-seed operations (used by both nnInteractive and local)
      _cachedContext = {
        viewport,
        element,
        scalarData,
        imageData,
        cols,
        rows,
        effectiveMin,
        effectiveMax,
        maxRadiusPx,
        smoothingIter: smoothing_iterations ?? 2,
      };
      _setCachedContext(_cachedContext);

      // ── nnInteractive path: plain click (first click) ─────────────────
      // User can opt out of nnInteractive via segEngineAtom (Shift+I toggles).
      // The reader study blocks it regardless of the stored engine (ENG-188).
      const store = getDefaultStore();
      const useNnInteractive =
        store.get(segEngineAtom) === "nnInteractive" && !store.get(nnInteractiveBlockedAtom);
      if (useNnInteractive && isNnInteractiveAvailable() && getNnContext()) {
        // The handler resets plain-grow AI instructions after snapshotting.
        // Keep the plane's session here: it owns earlier Undo checkpoints.

        // Fire async — the spinner overlay blocks further clicks
        handleMultiSeriesNnClick(
          worldPoint,
          clickedPlane,
          "positive",
          _cachedContext,
          _segmentIndex,
          _regionGrowImageIds,
          makeCommitDispatcher(viewport?.id ?? null),
          makeAutoSwitchDispatcher(viewport?.id ?? null),
        ).then((success) => {
          if (!success) {
            if (thresholdUndo && !thresholdUndo.isCurrent()) return;
            // Fallback to local flood fill
            console.warn("[nnInteractive:fallback] reason=gpu_unavailable");
            perfMark("nn-interactive-fallback");
            setNnFailedAt(Date.now());
            showNnToast("GPU service unavailable — using local segmentation");
            _seeds.push([row, col]);

            // Compute the primary's contour pixel area BEFORE updatePreview so we
            // can pass it to companions as a sanity-check cap. updatePreview will
            // re-run the same fill internally; the cost is one extra small flood
            // fill, which is negligible at stone scale.
            const primarySmoothed = _cachedContext ? runFillPipeline(_cachedContext)?.[0] : null;
            const primaryAreaPx = primarySmoothed ? polygonAreaPx(primarySmoothed) : 0;

            if (!applyThresholdPreview(thresholdUndo)) return;
            if (currentImageId) _regionGrowImageIds.add(currentImageId);

            // Track the originating plane so per-plane undo knows which
            // state to target. Companions populate `_fallbackPerPlane`
            // below; the primary stays in the global pipeline state.
            _fallbackPrimaryPlane = clickedPlane;

            // Ensure a session exists so the 3D viewer can display fallback contours.
            // Skip if series UID is unknown to avoid session cache collisions across studies.
            const sibs = getSiblingViewports();
            const seriesUid = sibs?.[clickedPlane]?.seriesUid;
            if (seriesUid) {
              ensureSessionForFallback(viewport, clickedPlane, seriesUid);
              refreshViewerFromAnnotations();
            }

            // Seed the two companion viewports with the same world point so a
            // single click yields up to 3 contours (parity with GPU companion path).
            // Pass the primary's seed HU + HU range + contour area so companions
            // stay constrained to stone density and don't engulf contrast-enhanced
            // parenchyma. Bump the cancellation token so a previous in-flight
            // propagation (if any) is cancelled before this one starts.
            const fallbackToken = bumpFallbackCompanionToken();
            void runFallbackOnCompanions(
              worldPoint,
              clickedPlane,
              seedHU,
              effectiveMin,
              effectiveMax,
              primaryAreaPx,
              fallbackToken,
            );
          }
        });
        return true;
      }

      // ── Local flood fill path (fallback or nnInteractive unavailable) ─
      _seeds.push([row, col]);

      // Compute primary contour area before preview so we can pass it as a
      // sanity-check cap to the companion-propagation pipeline (same approach
      // as the nnInteractive→local fallback above).
      const primarySmoothed = _cachedContext ? runFillPipeline(_cachedContext)?.[0] : null;
      const primaryAreaPx = primarySmoothed ? polygonAreaPx(primarySmoothed) : 0;

      if (!applyThresholdPreview(thresholdUndo)) return true;
      if (currentImageId) _regionGrowImageIds.add(currentImageId);

      // Mirror the nn-fallback companion-propagation flow so the local path
      // produces 3-plane contours from a single click — whether the user
      // explicitly chose threshold or nnInteractive is just unavailable.
      _fallbackPrimaryPlane = clickedPlane;
      const sibsLocal = getSiblingViewports();
      const seriesUidLocal = sibsLocal?.[clickedPlane]?.seriesUid;
      if (seriesUidLocal) {
        ensureSessionForFallback(viewport, clickedPlane, seriesUidLocal);
        refreshViewerFromAnnotations();
      }
      const fallbackTokenLocal = bumpFallbackCompanionToken();
      void runFallbackOnCompanions(
        worldPoint,
        clickedPlane,
        seedHU,
        effectiveMin,
        effectiveMax,
        primaryAreaPx,
        fallbackTokenLocal,
      );

      return true;
    }
  }

  // Store factory ref so session module's refineNnInteractive can call into the closure
  setHandleMultiSeriesNnClick((worldPoint, clickedPlane, label, ctx, isRefinement) => {
    const ctxViewportId: string | null = ctx?.viewport?.id ?? null;
    return handleMultiSeriesNnClick(
      worldPoint,
      clickedPlane,
      label,
      ctx,
      _segmentIndex,
      _regionGrowImageIds,
      makeCommitDispatcher(ctxViewportId),
      makeAutoSwitchDispatcher(ctxViewportId),
      isRefinement,
    );
  });

  return RegionGrowTool;
}

// ─── Reject / Undo (called from useViewerHotkeys) ──────────────────

/**
 * Reject the current preview — removes the contour annotation.
 * Returns true if a preview was rejected.
 */
export function rejectRegionGrowPreview(cornerstoneTools: any): boolean {
  if (!_previewUID && _fallbackPerPlane.size === 0) return false;
  removePreviewAnnotations(cornerstoneTools);
  // Also remove any propagated annotations
  for (const uid of _propagatedUIDs) {
    try {
      cornerstoneTools.annotation.state.removeAnnotation(uid);
    } catch (e) {
      logger.debug("[RegionGrowTool] removeAnnotation skipped:", e);
    }
  }
  // Remove all per-plane fallback contours (primary's primary-bucket entry
  // is intentionally redundant with _previewUID/_propagatedUIDs above —
  // removeAnnotation is idempotent on already-deleted UIDs).
  const elementsToRender = new Set<any>();
  _fallbackPerPlane.forEach((entry) => {
    if (entry.seedUID) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(entry.seedUID);
      } catch (e) {
        logger.debug("[RegionGrowTool] removeAnnotation skipped:", e);
      }
    }
    for (const uid of entry.propagatedUIDs) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch (e) {
        logger.debug("[RegionGrowTool] removeAnnotation skipped:", e);
      }
    }
    if (entry.element) elementsToRender.add(entry.element);
  });
  _propagatedUIDs = [];
  _fallbackPerPlane.clear();
  _fallbackPrimaryPlane = null;
  _previewUID = null;
  _previewExtraUIDs = [];
  _seeds.length = 0;
  _exclusions.length = 0;
  _regionGrowImageIds.clear();
  // Volume-guard strike count is per-stone, not per-case: a rejected oversized
  // attempt on case A must not push case B into the 2-strike fallback.
  resetConsecutiveOversizedCount();
  // Cancel any in-flight companion propagation so it can't commit annotations
  // into the now-cleared `_fallbackPerPlane` after the user has rejected.
  bumpFallbackCompanionToken();
  // Trigger SVG annotation re-render to clear the removed contours
  if (_cachedContext) {
    try {
      cornerstoneTools.utilities.triggerAnnotationRender(_cachedContext.element);
    } catch (e) {
      logger.debug("[RegionGrowTool] triggerAnnotationRender skipped:", e);
    }
  }
  elementsToRender.forEach((el) => {
    try {
      cornerstoneTools.utilities.triggerAnnotationRender(el);
    } catch (e) {
      logger.debug("[RegionGrowTool] per-plane triggerAnnotationRender skipped:", e);
    }
  });
  // Drop fallback sessions so hasNnInteractiveSession() returns false and
  // the hotkey dispatcher routes future undos to the regular grow path.
  getMultiSessions().clear();
  _cachedContext = null;
  _setCachedContext(null);
  return true;
}

/**
 * Undo the last seed or exclusion. Re-runs the fill pipeline.
 * Returns true if there was something to undo.
 */
export function undoRegionGrowSeed(cornerstoneTools: any): boolean {
  if (!_previewUID || !_cachedContext) return false;

  // Remove last exclusion first, then last seed
  if (_exclusions.length > 0) {
    _exclusions.pop();
  } else if (_seeds.length > 1) {
    // Don't remove the last seed — that would leave nothing
    _seeds.pop();
  } else {
    // Only one seed left — reject entirely
    return rejectRegionGrowPreview(cornerstoneTools);
  }

  // Remove old preview and re-run
  removePreviewAnnotations(cornerstoneTools);
  _previewUID = null;
  _previewExtraUIDs = [];

  const ctx = _cachedContext;
  const contours = runFillPipeline(ctx);
  if (!contours) {
    return rejectRegionGrowPreview(cornerstoneTools);
  }

  const seedImageId = ctx.viewport.getCurrentImageId?.();
  const worldContours: Pt3[][] = [];
  for (const contour of contours) {
    const worldPoints = toWorldPoints(contour, ctx.imageData.imageData, {
      viewport: ctx.viewport,
      imageId: seedImageId,
    });
    if (!worldPoints) return false;
    worldContours.push(worldPoints);
  }

  const uids: string[] = [];
  for (const worldPoints of worldContours) {
    const { annotation, uid } = buildContourAnnotation(worldPoints, ctx, {}, cornerstoneTools);

    cornerstoneTools.annotation.state.addAnnotation(annotation, ctx.element);
    try {
      cornerstoneTools.utilities.contourSegmentation.addContourSegmentationAnnotation(annotation);
    } catch (err) {
      console.error(
        "[RegionGrowTool] addContourSegmentationAnnotation failed — contour will be invisible:",
        err,
      );
    }
    try {
      cornerstoneTools.annotation.state.triggerAnnotationCompleted(annotation);
    } catch (e) {
      logger.debug("[RegionGrowTool] triggerAnnotationCompleted skipped:", e);
    }
    uids.push(uid);
  }
  _previewUID = uids[0];
  _previewExtraUIDs = uids.slice(1);
  try {
    cornerstoneTools.utilities.triggerAnnotationRender(ctx.element);
  } catch (e) {
    logger.debug("[RegionGrowTool] triggerAnnotationRender skipped:", e);
  }
  return true;
}

/**
 * Per-plane undo for the GPU fallback flow. Removes the seed contour and
 * any propagation contours for the user's currently active plane only —
 * leaves contours on other planes intact. Returns true if something was
 * removed.
 *
 * - Active plane is the originating click plane → tear down primary state
 *   (`_previewUID`, `_propagatedUIDs`, seeds, cached context) and the
 *   primary plane's fallback session.
 * - Active plane is a companion → remove its bucket from `_fallbackPerPlane`
 *   and drop its fallback session.
 *
 * If after removal no fallback contours remain anywhere, the function
 * also drops the lingering `_fallbackPrimaryPlane` flag and ensures the
 * tool is back to a clean slate.
 */
export function undoFallbackPlane(cornerstoneTools: any, plane: PlaneKey): boolean {
  // Only act when we're in fallback mode — otherwise let the caller fall
  // through to the regular undo paths.
  if (!isFallbackOnly()) return false;

  let removed = false;
  let renderTarget: any = null;

  if (plane === _fallbackPrimaryPlane) {
    // Tear down the primary's local-pipeline state.
    if (_previewUID) {
      removePreviewAnnotations(cornerstoneTools);
      removed = true;
    }
    for (const uid of _propagatedUIDs) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch (e) {
        logger.debug("[RegionGrowTool] undoFallbackPlane removeAnnotation skipped:", e);
      }
      removed = true;
    }
    renderTarget = _cachedContext?.element ?? null;
    _previewUID = null;
    _previewExtraUIDs = [];
    _previewImageId = null;
    _propagatedUIDs = [];
    _seeds.length = 0;
    _exclusions.length = 0;
    _cachedContext = null;
    _setCachedContext(null);
    getMultiSessions().delete(plane);
    _fallbackPrimaryPlane = null;
  } else {
    const entry = _fallbackPerPlane.get(plane);
    if (!entry) return false;
    if (entry.seedUID) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(entry.seedUID);
      } catch (e) {
        logger.debug("[RegionGrowTool] undoFallbackPlane removeAnnotation skipped:", e);
      }
      removed = true;
    }
    for (const uid of entry.propagatedUIDs) {
      try {
        cornerstoneTools.annotation.state.removeAnnotation(uid);
      } catch (e) {
        logger.debug("[RegionGrowTool] undoFallbackPlane removeAnnotation skipped:", e);
      }
      removed = true;
    }
    renderTarget = entry.element;
    _fallbackPerPlane.delete(plane);
    getMultiSessions().delete(plane);
  }

  if (renderTarget) {
    try {
      cornerstoneTools.utilities.triggerAnnotationRender(renderTarget);
    } catch (e) {
      logger.debug("[RegionGrowTool] undoFallbackPlane triggerAnnotationRender skipped:", e);
    }
  }

  // Final cleanup: if everything's gone, drop residual session state so the
  // hotkey dispatcher routes future undos correctly and the cursor returns
  // to its non-session state.
  if (
    !_previewUID &&
    _fallbackPerPlane.size === 0 &&
    !_fallbackPrimaryPlane &&
    getMultiSessions().size === 0
  ) {
    _regionGrowImageIds.clear();
  }

  return removed;
}

// ─── nnInteractive Undo / Reject (called from useViewerHotkeys) ─────────────
// Delegates to nnInteractiveSession module, passing _regionGrowImageIds.

/** Whether an nnInteractive session is active (for hotkey routing). */
export function hasNnInteractiveSession(): boolean {
  return _hasNnInteractiveSession();
}

/**
 * Undo the last nnInteractive refinement.
 * Returns true if something was undone.
 */
export function undoNnInteractive(cornerstoneTools: any, plane?: PlaneKey): boolean {
  return _undoNnInteractive(cornerstoneTools, _regionGrowImageIds, plane);
}

/**
 * Reject the entire nnInteractive segmentation.
 * Returns true if there was something to reject.
 */
export function rejectNnInteractive(cornerstoneTools: any): boolean {
  return _rejectNnInteractive(cornerstoneTools, _regionGrowImageIds);
}
